/**
 * Arabic (Modern Standard Arabic).
 *
 * Typed against the Italian dictionary like English, so a key added there and forgotten
 * here is a build error rather than a dotted path shown to a user.
 *
 * Conventions (shared with the field app's ar.ts so the two agree):
 *  - Western digits (0-9) everywhere; placeholders such as {count} are untouched.
 *  - "Clock in/out" is the shift: تسجيل الدخول للعمل / تسجيل الخروج من العمل. Signing in
 *    to the portal is always worded with "الحساب" so the two never blur.
 *  - Counts are written as "{count} موظف" (number first, singular noun). It is not strict
 *    classical agreement for every number, but it reads naturally for all of them.
 *  - Italian legal instruments (DURC, UNILAV, POS, DiCo, PES/PAV) keep their names.
 */

import type { Dictionary } from './index';

export const ar: Dictionary = {
  common: {
    allEmployees: 'جميع الموظفين',
    typeToSearch: 'اكتب للبحث…',
    noMatches: 'لا توجد نتائج مطابقة',
    searchPeople: 'ابحث بالاسم أو البريد الإلكتروني أو الوظيفة…',
    networkError: 'تعذّر الاتصال بالخادم. تحقق من اتصالك وحاول مرة أخرى.',
    databaseStarting: 'النظام قيد التشغيل. حاول مرة أخرى بعد بضع ثوانٍ.',
    tryAgain: 'حاول مرة أخرى',
    waking: 'جارٍ التشغيل، لحظة من فضلك…',
    charsLeftOne: 'يلزم حرف واحد إضافي.',
    charsLeftMany: 'يلزم {count} أحرف إضافية.',
    notAvailable: 'غير متاح',
    unknownError: 'خطأ غير معروف.',
    somethingWrong: 'حدث خطأ ما',
    backToList: 'العودة إلى القائمة',
    yes: 'نعم',
    no: 'لا',
    apply: 'تطبيق',
    start: 'البداية',
    end: 'النهاية',
    reasonRequired: 'السبب (مطلوب)',
    save: 'حفظ',
    cancel: 'إلغاء',
    confirm: 'تأكيد',
    areYouSure: 'هل أنت متأكد؟',
    notice: 'حدث خطأ ما',
    ok: 'حسنًا',
    close: 'إغلاق',
    delete: 'حذف',
    edit: 'تعديل',
    open: 'فتح',
    back: 'رجوع',
    all: 'الكل',
    none: 'لا شيء',
    loading: 'جارٍ التحميل…',
    saving: 'جارٍ الحفظ…',
    search: 'بحث',
    details: 'التفاصيل',
    download: 'تنزيل',
    from: 'من',
    to: 'إلى',
    day: 'اليوم',
    today: 'اليوم',
    tomorrow: 'غدًا',
    thisMonth: 'هذا الشهر',
    lastMonth: 'الشهر الماضي',
    thisWeek: 'هذا الأسبوع',
    onlyToday: 'اليوم فقط',
    employee: 'الموظف',
    employees: 'الموظفون',
    site: 'الموقع',
    sites: 'المواقع',
    job: 'العمل',
    jobs: 'الأعمال',
    company: 'الشركة',
    date: 'التاريخ',
    time: 'الوقت',
    status: 'الحالة',
    notes: 'ملاحظات',
    notesOptional: 'ملاحظات (اختياري)',
    optional: 'اختياري',
    required: 'مطلوب',
    hours: 'الساعات',
    error: 'حدث خطأ ما.',
    saveError: 'تعذّر الحفظ.',
    retry: 'حاول مرة أخرى',
    noResults: 'لا توجد نتائج.',
    noResultsHint: 'جرّب تغيير البحث أو عوامل التصفية.',
    clearFilters: 'مسح عوامل التصفية',
    select: 'اختر…',
    add: 'إضافة…',
    creating: 'جارٍ الإنشاء…',
    description: 'الوصف',
    priority: 'الأولوية',
    normal: 'عادية',
    onlyOffice: 'للمكتب فقط',
    allStaff: 'جميع العاملين',
    assignedEngineers: 'الفنيون المكلّفون',
    noJob: 'بلا عمل',
    outsideArea: 'خارج النطاق',
    daysLeft: '{count} يوم',
    daysAgo: 'منذ {count} يوم',
    address: 'العنوان',
    city: 'المدينة',
    latitude: 'خط العرض',
    longitude: 'خط الطول',
    postcode: 'الرمز البريدي',
    accessNotes: 'ملاحظات الدخول',
    manage: 'إدارة',
    assign: 'تكليف',
    remove: 'إزالة',
    field: 'الحقل',
    newValue: 'القيمة الجديدة',
    reason: 'السبب',
    reasonGiven: 'السبب المذكور',
    backToJobs: 'العودة إلى الأعمال',
    backToShifts: 'العودة إلى الورديات',
    shift: 'الوردية',
    clockInTime: 'وقت تسجيل الدخول للعمل',
    clockOutTime: 'وقت تسجيل الخروج من العمل',
    noSiteLinked: 'لا يوجد موقع مرتبط.',
    noShiftsRecorded: 'لا توجد ورديات مسجّلة.',
    noDocumentsAttached: 'لا توجد مستندات مرفقة.',
    noTeam: 'لم يُكلَّف أي فني.',
    noAdjustments: 'لا توجد تعديلات مسجّلة.',
    adjust: 'تعديل الوردية',
  },

  nav: {
    calendar: 'التقويم',
    leave: 'الإجازات والأذونات',
    more: 'المزيد',
    openMenu: 'فتح القائمة',
    closeMenu: 'إغلاق القائمة',
    inventory: 'المخزون',
    vehicles: 'المركبات',
    recycleBin: 'سلة المحذوفات',
    messages: 'الرسائل',
    portal: 'البوابة الداخلية',
    dashboard: 'لوحة المعلومات',
    groupOperations: 'العمليات',
    groupRecords: 'السجلات',
    groupCompliance: 'الامتثال',
    groupSystem: 'النظام',
    shifts: 'الورديات',
    assignments: 'التكليفات',
    payroll: 'الساعات والأجور',
    employees: 'الموظفون',
    sites: 'المواقع',
    jobs: 'الأعمال',
    alerts: 'تواريخ الانتهاء',
    compliance: 'المستندات المطلوبة',
    documents: 'مكتبة المستندات',
    audit: 'سجل النشاط',
    settings: 'الإعدادات',
  },

  theme: {
    label: 'المظهر',
    system: 'النظام',
    light: 'فاتح',
    dark: 'داكن',
  },

  locale: {
    label: 'اللغة',
    it: 'Italiano',
    en: 'English',
    ar: 'العربية',
  },

  table: {
    openRow: 'فتح',
    search: 'بحث',
    searchPlaceholder: 'بحث…',
    sortBy: 'ترتيب حسب',
    ascending: 'تصاعديًا',
    descending: 'تنازليًا',
    view: 'العرض',
    viewTable: 'جدول',
    viewCards: 'بطاقات',
    resultsOne: 'نتيجة واحدة',
    resultsMany: '{count} نتائج',
    filteredFrom: 'من أصل {total}',
  },

  dashboard: {
    title: 'لوحة المعلومات',
    onDutyNow: 'على رأس العمل الآن',
    jobsToday: 'أعمال اليوم',
    toReview: 'للمراجعة',
    toReviewHint: 'ورديات مُعلَّمة، آخر 30 يومًا',
    unassigned: 'غير مكلّفة',
    unassignedHint: 'أعمال مجدولة بلا فني',
    nobodyOnDuty: 'لا أحد على رأس العمل حاليًا.',
    shiftsToReview: 'ورديات للمراجعة',
    noShiftsToReview: 'لا توجد ورديات تحتاج إلى مراجعة.',
    expiriesToHandle: 'تواريخ انتهاء للمعالجة',
    noExpiries: 'لا شيء للمعالجة. كل المستندات سليمة.',
    seeAll: 'عرض الكل',
    jobsWithoutEngineer: 'أعمال بلا فني',
    allJobsAssigned: 'كل عمل مجدول له فني مكلّف.',
    allClear: 'كل شيء على ما يرام',
    allClearHint: 'لا شيء يحتاج إلى انتباه.',
  },

  shifts: {
    jobAndSite: 'العمل · الموقع',
    noJob: 'بلا عمل',
    inPosition: 'موقع تسجيل الدخول للعمل',
    outPosition: 'موقع تسجيل الخروج من العمل',
    openInMap: 'فتح في الخريطة',
    siteArea: 'نطاق الموقع',
    needsReview: 'للمراجعة',
    review: 'تحديد كمُراجَعة',
    reviewTitle: 'مراجعة هذه الوردية',
    reviewExplainer:
      'أكّد أن الوردية صحيحة كما سُجّلت. تبقى العلامات ظاهرة — فهي دليل ما حدث — لكن الوردية تغادر قائمة المراجعة وتُحتسب ضمن الساعات.',
    reviewNote: 'ملاحظة (اختياري)',
    reviewNotePlaceholder: 'مثال: تم التأكيد هاتفيًا مع الموظف.',
    reviewedBy: 'راجعها {name}، {date}',
    withdrawReview: 'التراجع عن المراجعة',
    reviewedBadge: 'تمت مراجعتها',
    queueEmpty: 'لا توجد ورديات للمراجعة.',
    onlyNeedsReview: 'للمراجعة فقط',
    breakShort: 'استراحة {count} د',
    recordedDuration: 'المدة المسجّلة',
    inProgressSuffix: '· قيد التنفيذ',
    deviceEventId: 'معرّف الحدث على الجهاز',
    locationHint1:
      'لا يُسجَّل الموقع إلا عند تسجيل الدخول للعمل وتسجيل الخروج منه، ولا يُسجَّل أبدًا أثناء الوردية.',
    locationHint2:
      'يحسب الخادم النتيجة من الإحداثيات الخام، وليس الجهاز.',
    adjustments: 'التغييرات',
    noAdjustmentsHint: 'لا توجد تغييرات. الوردية كما سُجّلت تمامًا.',
    adjustmentSaved: 'تم حفظ التغيير.',
    saveAdjustment: 'حفظ التغيير',
    breakMinutes: 'الاستراحة (بالدقائق)',
    correctHint: 'يُسجَّل كل تغيير مع سببه ويظهر للموظف.',
    clockIn: 'وقت تسجيل الدخول للعمل',
    clockOut: 'وقت تسجيل الخروج من العمل',
    job: 'العمل',
    locationData: 'بيانات الموقع',
    inOutcome: 'تسجيل الدخول للعمل · النتيجة',
    inDistance: 'تسجيل الدخول للعمل · المسافة',
    outOutcome: 'تسجيل الخروج من العمل · النتيجة',
    outDistance: 'تسجيل الخروج من العمل · المسافة',
    noGps: 'لا يوجد GPS',
    correct: 'تصحيح هذه الوردية',
    reasonPlaceholder: 'مثال: أكّد الموظف الأوقات هاتفيًا',
    title: 'الورديات',
    subtitle: 'تسجيلات الدخول للعمل وبيانات الموقع والتعديلات',
    exportCsv: 'تصدير CSV',
    csvIn: 'تسجيل الدخول للعمل',
    csvOut: 'تسجيل الخروج من العمل',
    csvBreak: 'الاستراحة (د)',
    csvDistance: 'المسافة (م)',
    csvReason: 'السبب',
    flaggedOnly: 'المُعلَّمة فقط',
    searchPlaceholder: 'الموظف، السبب، العلامة…',
    includeUnreviewed: 'تضمين غير المُراجَعة في التصدير',
    includeUnreviewedHint: 'يشمل الورديات المُغلقة تلقائيًا ومحل النزاع',
    countOne: 'وردية واحدة',
    countMany: '{count} ورديات',
    reviewableTotal: 'الإجمالي القابل للمراجعة:',
    position: 'الموقع',
    duration: 'المدة',
    flags: 'العلامات',
    onSite: 'في الموقع',
    gpsUnavailable: 'لا يوجد GPS',
    excludedOne:
      'استُبعدت وردية واحدة من الإجمالي والتصدير لأنها أُغلقت تلقائيًا أو محل نزاع. تحتاج إلى مراجعة قبل دفع أجرها.',
    excludedMany:
      'استُبعدت {count} ورديات من الإجمالي والتصدير لأنها أُغلقت تلقائيًا أو محل نزاع. تحتاج إلى مراجعة قبل دفع أجرها.',
  },

  assignments: {
    notesOptional: 'ملاحظات (اختياري)',
    title: 'تكليفات المواقع',
    subtitle:
      'من يعمل أين. لا يستطيع الموظف تسجيل الدخول للعمل إلا في الموقع المكلَّف به في ذلك اليوم.',
    create: 'تكليف جديد',
    createTitle: 'تكليف طاقم بموقع',
    selectSite: 'اختر…',
    jobOptional: 'العمل (اختياري)',
    jobOptionalHint: 'مطلوب فقط لاحتساب الساعات على عمل معيّن.',
    noJob: 'بلا',
    startsOn: 'من',
    endsOn: 'إلى',
    openEnded: 'تكليف للمشروع (بلا تاريخ انتهاء)',
    project: 'المشروع',
    selectedCount: 'الموظفون (تم اختيار {count})',
    notesPlaceholder: 'مثال: وردية مسائية، الدخول من الطريق الجانبي',
    assignOne: 'تكليف موظف واحد',
    assignMany: 'تكليف {count} موظفين',
    assigning: 'جارٍ التكليف…',
    createError: 'تعذّر إنشاء التكليف.',
    unassignedOne: 'موظف واحد بلا موقع في {date}',
    unassignedMany: '{count} موظفين بلا موقع في {date}',
    unassignedHint: 'بدون تكليف يُسجَّل الدخول للعمل لكنه يُعلَّم كاستثناء.',
    noneForDay: 'لا توجد تكليفات في {date}.',
    area: 'نطاق {radius} م',
    noJobShort: 'بلا عمل',
    remove: 'إزالة',
    removeConfirm:
      'إزالة {name} من {site}؟ لا تتأثر الورديات المسجّلة عليه بالفعل.',
  },

  payroll: {
    title: 'الساعات والأجور',
    subtitle: 'الساعات لكل موظف ولكل موقع، قابلة للتصدير للمحاسب',
    downloadPdf: 'تنزيل PDF',
    downloadExcel: 'تنزيل Excel',
    preparing: 'جارٍ التحضير…',
    groupBy: 'تجميع حسب',
    byWorker: 'الموظف',
    bySite: 'الموقع',
    includeUnreviewed: 'تضمين الورديات غير المُراجَعة',
    totalHours: 'إجمالي الساعات',
    decimalHoursHint: '{value} ساعة عشرية',
    countedShifts: 'الورديات المحتسبة',
    excluded: 'المستبعدة',
    toVerify: 'للمراجعة',
    decimalHours: 'الساعات العشرية',
    noShifts: 'لا توجد ورديات في الفترة المحددة.',
    exportFailed: 'فشل التصدير.',
    detail: 'التفاصيل',
    hide: 'إخفاء',
    autoClosed: 'أُغلقت تلقائيًا',
    disputed: 'محل نزاع',
    outsideArea: 'خارج النطاق',
    excludedOne:
      'استُبعدت وردية واحدة من الإجمالي والتصدير لأنها أُغلقت تلقائيًا أو محل نزاع. راجعها في صفحة الورديات قبل دفع أجرها.',
    excludedMany:
      'استُبعدت {count} ورديات من الإجمالي والتصدير لأنها أُغلقت تلقائيًا أو محل نزاع. راجعها في صفحة الورديات قبل دفع أجرها.',
  },

  personal: {
    heading: 'البيانات الشخصية',
    photo: 'صورة البطاقة',
    uploadPhoto: 'رفع صورة',
    changePhoto: 'تغيير الصورة',
    removePhoto: 'إزالة',
    photoFailed: 'تعذّر رفع الصورة.',
    edit: 'تعديل',
    save: 'حفظ',
    cancel: 'إلغاء',
    saveFailed: 'تعذّر الحفظ.',
    firstName: 'الاسم الأول',
    lastName: 'اسم العائلة',
    jobTitle: 'المسمى الوظيفي',
    phone: 'الهاتف',
    dateOfBirth: 'تاريخ الميلاد',
    birthPlace: 'مكان الميلاد',
    taxCode: 'الرمز الضريبي',
    hiredOn: 'تاريخ التعيين',
    badgeNumber: 'رقم البطاقة',
    address: 'العنوان',
    emergencyContact: 'جهة اتصال للطوارئ',
  },

  employees: {
    assignmentRange: 'من {from} إلى {to}',
    assignmentOpen: 'من {from} — طوال المشروع',
    hoursRecent: 'الساعات (آخر 30 وردية)',
    title: 'الموظفون',
    subtitle: 'تُنشأ حسابات التطبيق هنا — لا يوجد تسجيل ذاتي',
    create: 'موظف جديد',
    createHint:
      'عند إدخال بريد إلكتروني تصله دعوة بكلمة مرور مؤقتة. وبدونه يُسجَّل الموظف فقط؛ أنشئ الحساب لاحقًا.',
    addOnly: 'إضافة موظف',
    createAccount: 'إنشاء حساب',
    firstName: 'الاسم الأول',
    lastName: 'اسم العائلة',
    phone: 'الهاتف',
    searchPlaceholder: 'الاسم، البريد الإلكتروني، الوظيفة…',
    countOne: 'موظف واحد',
    countMany: '{count} موظفين',
    role: 'الدور',
    createdAt: 'تاريخ الإنشاء',
    email: 'البريد الإلكتروني',
    name: 'الاسم',
    suspend: 'إيقاف',
    reactivate: 'إعادة التفعيل',
    backToList: 'العودة إلى القائمة',
    hoursLast30: 'الساعات (آخر 30 وردية)',
    shiftsRecorded: 'الورديات المسجّلة',
    withFlags: 'المُعلَّمة',
    documentsToHandle: 'مستندات للمعالجة',
    ofExpected: 'من أصل {total} متوقعة',
    recentShifts: 'الورديات الأخيرة',
    noShifts: 'لا توجد ورديات مسجّلة.',
    activeAssignments: 'التكليفات الحالية',
    noAssignments: 'لا يوجد موقع مكلَّف به.',
    assign: 'تكليف',
    documentFile: 'ملف المستند',
    manage: 'إدارة',
    fromDate: 'من {date}',
    toDate: 'إلى {date}',
    onProject: '— طوال المشروع',
    privacy: 'الخصوصية',
    locationNotice: 'إشعار الموقع:',
    noticeRead: 'قُرئ في {date}',
    noticeUnread: 'لم يُقرأ بعد',
    noticeExplainer:
      'إقرار بالاطلاع عليه، وليس موافقة: الأساس القانوني هو عقد العمل والمصلحة المشروعة.',
    onFile: 'محفوظ',
    notOnFile: 'غير محفوظ',
    expiresOn: 'ينتهي في {date}',
  },

  sites: {
    clientHint: 'الجهة التي يُنفَّذ العمل لأجلها. ترث الأعمال في هذا الموقع هذه الجهة.',
    noClient: '— بلا عميل —',
    newClient: '+ عميل جديد…',
    newClientPlaceholder: 'اسم العميل',
    deleteTitle: 'حذف هذا الموقع؟',
    deleteConfirm:
      'ينتقل “{name}” إلى سلة المحذوفات ويمكن استعادته خلال 30 يومًا. لا تتأثر الورديات السابقة.',
    tolerances: 'هوامش تسجيل الدخول للعمل',
    tolerancesHint:
      'اتركها فارغة لاستخدام الإعدادات العامة. املأها فقط للمواقع الكبيرة جدًا — المطارات ومراكز البيانات والمجمعات اللوجستية — حيث لا يمكن أن يكون الدبوس قريبًا من كل مكان عمل.',
    edgeTolerance: 'هامش تجاوز الحدود (م)',
    edgeToleranceHint:
      'عدد الأمتار المسموح بها خارج النطاق قبل تعليم تسجيل الدخول للعمل. يختلف عن توسيع نصف القطر: نصف القطر يحدد حجم الموقع، وهذا يحدد مقدار التسامح عند حافته.',
    trustOverride: 'الدقة الموثوقة (م)',
    rejectOverride: 'الدقة غير الصالحة (م)',
    overrideEmpty: 'عامّ',
    allowOutsideOverride: 'تسجيل الدخول للعمل خارج النطاق',
    useGlobal: 'استخدام الإعداد العام',
    forceAllow: 'مسموح دائمًا (مع ذكر السبب)',
    forceDeny: 'غير مسموح أبدًا',
    largeSiteWarning:
      'موقع كبير: عند تجاوز نصف قطر نحو 500 م يُفضَّل رسم الحدود الفعلية بدلًا من توسيع الدائرة، وإلا شمل النطاق الجيران أيضًا.',
    collapse: 'تصغير الخريطة',
    dropPin: 'ضع الدبوس',
    dropPinActive: 'انقر على الخريطة لوضع الدبوس.',
    movePinHint: 'اسحب الدبوس لتحريكه.',
    mapModifier: 'اضغط Shift لتحريك الخريطة أو تكبيرها.',
    expand: 'تكبير الخريطة',
    pinOutside: 'يجب أن يكون الدبوس داخل النطاق — حرّكه أو أعد الرسم.',
    pinLocked: 'لا يمكن للدبوس مغادرة النطاق المرسوم.',
    done: 'تم',
    drawBoundary: 'رسم الحدود',
    editBoundary: 'تعديل الحدود',
    clearBoundary: 'إزالة الحدود',
    finishBoundary: 'حفظ الحدود',
    undoPoint: 'التراجع عن نقطة',
    boundaryActive: 'تم رسم الحدود بـ {count} نقطة — وهي تحلّ محل نصف القطر.',
    radiusActive: 'لم تُرسم حدود — يسري نصف القطر {count} م.',
    pointsPlaced: '{count} نقطة. انقر لإضافة المزيد.',
    drawHint: 'انقر على الخريطة لتحديد النطاق. يلزم {count} نقطة إضافية.',
    noCoordinates: 'لا توجد إحداثيات — لن يتحرك الدبوس.',
    addressSearch: 'ابحث عن العنوان',
    addressSearchHint: 'اكتب واختر من القائمة: يملأ المدينة والرمز البريدي والإحداثيات.',
    addressPlaceholder: 'مثال: Via Roma 42, Milano',
    mapLabel: 'الموقع ونطاق تسجيل الدخول للعمل',
    namePlaceholder: 'مثال: Residenza Aurora — محطة فرعية',
    radiusLabel: 'نصف قطر النطاق (م)',
    newSite: 'موقع جديد',
    radiusHint: 'بين 25 و5000. الشقة والمستودع ليسا بالحجم نفسه.',
    accessNotes: 'ملاحظات الدخول',
    accessNotesHint:
      'رموز البوابات، مواقف السيارات، من يُسأل عنه. إنه أنفع حقل في التطبيق عند الموقع.',
    title: 'المواقع',
    subtitle: 'الإحداثيات ونطاق تسجيل الدخول للعمل',
    create: 'موقع جديد',
    edit: 'تعديل الموقع',
    geofenceWarning:
      'تحدد الإحداثيات ونصف القطر نتيجة كل تسجيل دخول للعمل مستقبلًا في هذا الموقع. يُكتب كل تغيير في سجل النشاط.',
    client: 'العميل',
    address: 'العنوان',
    coordinates: 'الإحداثيات',
    area: 'النطاق',
    missing: 'ناقص',
    radius: 'نصف قطر النطاق (م)',
    searchPlaceholder: 'الاسم، العميل، العنوان…',
    countOne: 'موقع واحد',
    countMany: '{count} مواقع',
  },

  jobs: {
    assignTo: 'تكليف',
    assignToHint: 'يظهر العمل في تطبيقهم ويصلهم إشعار.',
    saveAsDraft: 'حفظ كمسودة',
    saveAsDraftHint: 'المسودة غير مؤكدة بعد: تظهر في التطبيق لكن لا يستطيع أحد تسجيل الدخول للعمل عليها.',
    siteArea: 'نطاق الموقع: {radius} م',
    attachments: 'المرفقات',
    attachmentsHint:
      'المخططات والعقود وتقييمات المخاطر. يراها الفنيون المكلّفون في التطبيق.',
    attachedCount: '{count} ملف للإرفاق',
    workingHours: 'قواعد تسجيل الدخول للعمل',
    workingHoursHint:
      'لا تنطبق هذه القواعد إلا إذا كان للعمل موعد مخطط. اتركها على الإعداد العام ما لم يتطلب هذا العمل شيئًا مختلفًا.',
    earlyLabel: 'تسجيل الدخول للعمل مبكرًا',
    lateLabel: 'تسجيل الخروج من العمل متأخرًا',
    useGlobal: 'استخدام الإعداد العام',
    allowIt: 'مسموح',
    blockIt: 'محظور',
    noScheduleNote:
      'بدون موعد مخطط لا تنطبق هذه القواعد — إذ لا يوجد “قبل” ولا “بعد”.',
    noTeamWarning: 'لم يُكلَّف أي فني — لا يظهر هذا العمل في تطبيق أحد.',
    signatureRequired: 'التوقيع مطلوب',
    changeStatus: 'تغيير الحالة',
    finalState: 'هذا العمل في حالة نهائية ولا يمكن تغييره.',
    description: 'الوصف',
    access: 'الدخول',
    siteNotes: 'ملاحظات من الموقع',
    scheduling: 'الجدولة',
    statusChangeFailed: 'تعذّر تغيير الحالة.',
    descriptionPlaceholder: 'مثال: استبدال لوحة التوزيع الرئيسية',
    title: 'الأعمال',
    subtitle: 'الأعمال المجدولة وقيد التنفيذ والمغلقة',
    reference: 'المرجع',
    jobTitle: 'العنوان',
    urgent: 'عاجل',
    scheduledStart: 'بداية الموعد',
    scheduledEnd: 'نهاية الموعد',
    createJob: 'إنشاء عمل',
    shiftsOnJob: 'ورديات هذا العمل',
    searchPlaceholder: 'المرجع، العنوان، العميل…',
    countOne: 'عمل واحد',
    countMany: '{count} أعمال',
    client: 'العميل',
    priority: 'الأولوية',
    scheduled: 'مجدول',
    team: 'الفريق',
  },

  documents: {
    changeVisibility: 'الظهور',
    changeVisibilityTitle: 'من يستطيع رؤية “{name}”',
    changeVisibilityHint:
      'يسري التضييق فورًا. المستند المفتوح عند أحدهم يبقى مفتوحًا حتى يعيد التحميل.',
    customCategory: 'تسميتك الخاصة',
    customCategoryHint:
      'سمّه كما تشاء. تظهر في القائمة بدل “أخرى” ويمكن البحث بها.',
    customCategoryPlaceholder: 'مثال: وثيقة التأمين',
    visibilityHint: 'من يستطيع رؤية هذا المستند في التطبيق.',
    uploadDone: 'تم رفع الملف.',
    dropHere: 'أفلت ملفًا هنا، أو انقر للاختيار',
    accepted: 'PDF وP7M وJPG وPNG وHEIC وWord وExcel وOpenDocument · الحد الأقصى 50 ميغابايت (25 ميغابايت للصور)',
    uploading: 'جارٍ الرفع…',
    uploadFailed: 'فشل الرفع.',
    typeRejected: 'هذه الصيغة غير مقبولة. استخدم PDF أو P7M أو JPG أو PNG أو HEIC أو Word أو Excel أو OpenDocument.',
    tooLarge: 'الملف كبير جدًا — الحد الأقصى {size}.',
    uploadFor: 'رفع ملف لـ “{name}”',
    addDocument: 'رفع مستند',
    chooseCategory: 'الفئة',
    title: 'المستندات',
    subtitle: 'العقود والمخططات والشهادات والمواد الواردة من الموقع',
    searchPlaceholder: 'اسم الملف',
    category: 'الفئة',
    visibility: 'الظهور',
    size: 'الحجم',
    uploaded: 'تاريخ الرفع',
    file: 'الملف',
    countOne: 'مستند واحد',
    countMany: '{count} مستندات',
    deleteConfirm: 'حذف {name} نهائيًا؟ يبقى الإجراء مسجّلًا في سجل النشاط.',
  },

  folders: {
    addFile: 'إضافة ملف (مثل الوجه الخلفي)',
    addFileTitle: 'إضافة ملف إلى {label}',
    addFileHint: 'ينضم إلى المستند المحفوظ بالتواريخ نفسها: الوجه الخلفي لبطاقة الهوية، أو صفحة ثانية.',
    dropSides: 'اسحب الملف إلى هنا، أو الوجهين الأمامي والخلفي معًا، أو انقر للاختيار',
    addAnotherSide: 'إضافة ملف آخر إلى هذا المستند (مثل الوجه الخلفي)',
    sidesTogether: 'ستكون هذه الملفات الـ {count} مستندًا واحدًا (مثل الوجه الأمامي والخلفي).',
    fileCount: '{count} ملفات',
    showFiles: 'عرض كل الملفات ({count})',
    hideFiles: 'إخفاء الملفات',
    foldersHintVehicle: 'فواتير الصيانة والمخالفات وتقارير الحوادث وأي شيء آخر. انقر على مجلد لفتحه.',
    newFolderHintVehicle: 'يظهر المجلد لكل مركبة.',
    newDocumentHintVehicle:
      'يُضاف إلى قائمة كل مركبة، مثل "Telepass" أو "بطاقة الوقود". ستصلك تذكيرات قبل انتهائه.',
    vehiclePlaceholder: 'مثال: بطاقة الوقود',
    mandatoryVehicleHint: 'يظهر كناقص لكل مركبة قيد الاستخدام لا تملكه.',
    correct: 'تصحيح التواريخ والتفاصيل',
    correctTitle: 'تصحيح — {name}',
    correctHint: 'لتصحيح خطأ. إذا جُدِّد المستند فعلًا فاستخدم «تجديد» بدلًا من ذلك.',
    replaceFile: 'استبدال الملف',
    attachFile: 'إرفاق ملف',
    shortCode: 'رمز اسم الملف',
    shortCodeHint: 'تُعاد تسمية الملفات المرفوعة تلقائيًا، مثل {example}. اتركه فارغًا لاستخدام الاسم.',
    autoNameHint: 'يُعاد تسمية الملف تلقائيًا بالمستند والاسم وتاريخ الانتهاء.',
    replaceTitle: 'استبدال الملف — {name}',
    attachTitle: 'إرفاق ملف — {name}',
    replaceHint:
      'يبقى المستند نفسه — بتواريخه وسجله؛ يتغير الملف فقط. يبقى الملف السابق محفوظًا في سجل النشاط.',
    editItem: 'إعادة تسمية العنصر أو تغييره',
    removeItem: 'إزالة من القائمة',
    removedTitle: 'العناصر المُزالة ({count}) — عرض',
    restore: 'استعادة',
    documentsTitle: 'المستندات',
    documentsHint: 'نسخة حالية واحدة لكل مستند. الرفع مرة أخرى يجدّده، وتُؤرشف القديمة.',
    summary: '{present} من أصل {required} مستندات مطلوبة محفوظة',
    expiredCount: '{count} منتهٍ',
    expiringCount: '{count} ينتهي قريبًا',
    noDocuments: 'لا توجد مستندات في هذه القائمة بعد.',
    foldersTitle: 'المجلدات',
    foldersHint: 'ما تحتاجه من ملفات — دورات وتعيينات وأي شيء آخر. انقر على مجلد لفتحه.',
    foldersHintCompany: 'ما تحتاجه من ملفات — سياسات وعقود وتقارير. انقر على مجلد لفتحه.',
    noFolders: 'لا توجد مجلدات بعد.',
    newDocument: 'مستند جديد',
    addCompanyDocument: 'إضافة مستند للشركة',
    newFolder: 'مجلد جديد',
    newSubfolder: 'مجلد فرعي جديد',
    upload: 'رفع',
    more: 'إجراءات أخرى',
    edit: 'تعديل',
    rename: 'إعادة تسمية',
    history: 'عرض النسخ السابقة',
    hideHistory: 'إخفاء النسخ السابقة',
    current: 'الحالية',
    notOnFile: 'غير محفوظ',
    noScan: 'مسجّل بدون ملف',
    emptyShort: 'فارغ',
    emptyFolder: 'هذا المجلد فارغ.',
    countOne: 'ملف واحد',
    countMany: '{count} ملفات',
    subCountOne: 'مجلد فرعي واحد',
    subCountMany: '{count} مجلدات فرعية',
    dropInto: 'اسحب الملفات إلى هنا لإضافتها إلى “{name}”، أو انقر للاختيار',
    deleteFolderConfirm: 'حذف المجلد “{name}”؟ لا يمكن حذف إلا المجلد الفارغ.',
    deleteDocumentConfirm:
      'إزالة “{name}” من القائمة؟ ممكن فقط ما دام لا يوجد ملف محفوظ. يمكنك استعادته لاحقًا.',
    uploadTitle: 'رفع — {label}',
    multipleExplainer: 'يُضاف إلى المجلد؛ ولا يُستبدل شيء موجود فيه.',
    renewExplainer: 'تُؤرشف النسخة الحالية ولا يُكتب فوقها.',
    dropMany: 'اسحب الملفات إلى هنا، أو انقر للاختيار — يمكنك اختيار عدة ملفات دفعة واحدة',
    dropOne: 'اسحب الملف إلى هنا، أو انقر لاختياره',
    addMore: 'إضافة ملفات أخرى',
    uploadN: 'رفع {count} ملفات',
    uploadingProgress: 'جارٍ رفع {done} من {total}…',
    fileFailed: 'لم يُرفع “{name}”: {reason}',
    noFileHint: 'لا يوجد ملف بعد؟ احفظ التواريخ الآن وأرفق الصورة الممسوحة لاحقًا.',
    notes: 'ملاحظات',
    editTitle: 'تعديل — {name}',
    subfolderTitle: 'مجلد فرعي جديد في “{name}”',
    newFolderHintWorker: 'يظهر المجلد في ملف كل موظف.',
    newFolderHintCompany: 'مكان لأي أوراق خاصة بالشركة.',
    newDocumentHintWorker:
      'يُضاف إلى قائمة كل موظف، مثل “رخصة القيادة C”. ستصلك تذكيرات قبل انتهائه.',
    newDocumentHintCompany:
      'سمّه ثم أرفق الملف وموعده النهائي. ستصلك تذكيرات قبل انتهائه.',
    createAndUpload: 'إنشاء ورفع',
    createFolder: 'إنشاء مجلد',
    name: 'الاسم',
    description: 'الوصف',
    folderPlaceholder: 'مثال: التعيينات',
    workerPlaceholder: 'مثال: رخصة القيادة C',
    companyPlaceholder: 'مثال: شهادة السلامة من الحريق',
    tracksExpiry: 'له تاريخ انتهاء',
    tracksExpiryHint: 'يُطلب التاريخ عند الرفع، ويصل تنبيه قبل حلوله.',
    warnDays: 'ذكّرني قبل هذا العدد من الأيام',
    mandatory: 'مطلوب',
    mandatoryWorkerHint: 'يظهر كناقص لكل موظف لا يملكه.',
    mandatoryCompanyHint: 'يظهر كناقص إلى أن يُحفظ.',
  },

  viewer: {
    preview: 'معاينة',
    openTab: 'فتح في تبويب جديد',
    previous: 'السابق',
    next: 'التالي',
    noPreview: 'لا توجد معاينة لهذه الصيغة',
    noPreviewHint: 'تُفتح ملفات Word وExcel وصور HEIC وملفات .p7m الموقّعة في برامجها الخاصة. نزّله لتطلع عليه.',
    failed: 'تعذّر تحميل المعاينة.',
  },

  send: {
    button: 'إرسال المستندات',
    buttonCount: 'إرسال المستندات ({count})',
    select: 'تحديد {name}',
    selectAll: 'تحديد كل المعروضين',
    clearSelection: 'مسح التحديد',
    title: 'إرسال المستندات',
    hint: 'اختر الأشخاص والمستندات، ثم أرسلها بالبريد الإلكتروني إلى العميل أو نزّل ملف ZIP.',
    who: 'من',
    selectedCount: 'تم تحديد {count}',
    which: 'أي مستندات',
    employeeDocuments: 'مستندات الموظفين',
    companyDocuments: 'مستندات الشركة',
    all: 'تحديد الكل',
    none: 'مسح',
    summaryOne: 'مستند واحد · {size}',
    summary: '{count} مستندات · {size}',
    showFiles: 'عرض القائمة',
    missing: '{count} غير محفوظة:',
    tooBig: 'كبير جدًا لرسالة واحدة. قسّمه إلى عمليتي إرسال، أو نزّل ملف ZIP.',
    toWhom: 'إرسال إلى',
    recipients: 'البريد الإلكتروني للعميل',
    recipientsHint: 'حتى 5 عناوين، مفصولة بفواصل.',
    subject: 'الموضوع',
    message: 'الرسالة',
    defaultSubject: 'مستندات العاملين - HH Electrical Systems',
    defaultMessage:
      'صباح الخير،\nتجدون مرفقًا المستندات المطلوبة للدخول إلى الموقع.\nمع خالص التحية',
    copyToMe: 'أرسل لي نسخة',
    privacy:
      'تحتوي هذه المستندات على بيانات شخصية: أرسل فقط ما طلبه العميل. يُسجَّل كل مستند مُرسل في سجل النشاط.',
    emailPending:
      'يبدأ إرسال البريد الإلكتروني من البوابة بمجرد أن توافق AWS على خدمة البريد. وإلى ذلك الحين نزّل ملف ZIP وأرفقه برسالة من بريدك الخاص.',
    sendEmail: 'إرسال البريد الإلكتروني',
    sending: 'جارٍ الإرسال…',
    downloadZip: 'تنزيل ZIP',
    preparing: 'جارٍ التحضير…',
    zipReady: 'ملف ZIP جاهز: {count} مستندات، {size}. بدأ التنزيل.',
    sent: 'أُرسلت {count} مستندات إلى {to}.',
  },

  vehicles: {
    drivingTitle: 'مركبة الشركة',
    drivingNone: 'لا توجد مركبة قيد الاستخدام.',
    title: 'المركبات',
    subtitle: 'مركبات الشركة: من يقودها، وهل أوراقها سليمة',
    add: 'إضافة مركبة',
    addHint: 'رقم اللوحة وحده مطلوب. يمكن ملء الباقي لاحقًا.',
    editTitle: 'تعديل {plate}',
    searchPlaceholder: 'اللوحة، الطراز، السائق…',
    countOne: 'مركبة واحدة',
    countMany: '{count} مركبات',
    empty: 'لا توجد مركبات بعد.',
    vehicle: 'المركبة',
    plate: 'اللوحة',
    name: 'الاسم المختصر',
    nameHint: 'كما يسمّيها المكتب، مثل "الشاحنة البيضاء".',
    make: 'الصانع',
    model: 'الطراز',
    year: 'السنة',
    fuel: 'الوقود',
    registeredOn: 'أول تسجيل',
    km: 'كم',
    kmNow: 'الكيلومترات الآن',
    kmHint: 'من لوحة العدادات. اختياري، لكنه ما يحتاجه موعد الصيانة وبطاقة الوقود.',
    vin: 'رقم الهيكل (VIN)',
    notes: 'ملاحظات',
    holder: 'السائق',
    driver: 'السائق',
    nobody: 'لا أحد',
    since: 'منذ {date}',
    dueBack: 'تُعاد في {date}',
    dueBackOn: 'تاريخ الإعادة',
    inUse: 'قيد الاستخدام',
    documents: 'المستندات',
    documentsTitle: 'مستندات المركبة',
    nextExpiry: 'أقرب انتهاء',
    nextExpiryOn: 'أقرب انتهاء {date}',
    docsOk: 'سليمة',
    docsExpired: '{count} منتهٍ',
    docsExpiring: '{count} ينتهي قريبًا',
    docsMissing: '{count} ناقص',
    details: 'التفاصيل',
    historyTitle: 'من استخدمها',
    noHistory: 'لم يقدها أحد بعد.',
    from: 'من',
    to: 'إلى',
    assign: 'تكليف',
    handOver: 'تسليم',
    return: 'إعادة',
    assignTitle: 'تكليف {plate}',
    handOverTitle: 'تسليم {plate} من {name}',
    handOverHint: 'ينهي استخدام السائق الحالي ويبدأ استخدام الجديد في خطوة واحدة.',
    returnTitle: 'إعادة {plate} من {name}',
    toGarage: 'تذهب مباشرة إلى الورشة (صيانة)',
    assignNotesPlaceholder: 'مثال: موقع ميلانو، مع بطاقة الوقود',
    returnNotesPlaceholder: 'مثال: خدش في المصد الخلفي',
    deleteConfirm: 'حذف {plate}؟ يبقى سجلها ومستنداتها محفوظين.',
    status: {
      available: 'متاحة',
      assigned: 'قيد الاستخدام',
      maintenance: 'في الصيانة',
      retired: 'خارج الخدمة',
    },
    fuels: {
      petrol: 'بنزين',
      diesel: 'ديزل',
      electric: 'كهربائي',
      hybrid: 'هجين',
      lpg: 'غاز البترول المسال',
      methane: 'ميثان',
    },
  },

  rename: {
    title: 'إعادة تسمية الملف',
    name: 'الاسم الجديد',
    extensionKept: 'يبقى الامتداد {ext} كما هو، حتى يُفتح الملف بالبرنامج الصحيح.',
    file: 'إعادة تسمية الملف',
    short: 'إعادة تسمية',
  },

  exempt: {
    notRequiredPerson: 'غير مطلوب لهذا الشخص',
    notRequiredVehicle: 'غير مطلوب لهذه المركبة',
    requiredAgain: 'مطلوب من جديد',
    confirm:
      'لن يظهر "{name}" كناقص ولن يثير تنبيهات لهم بعد الآن. يمكنك جعله مطلوبًا من جديد في أي وقت. هل تتابع؟',
  },

  sheet: {
    export: 'تصدير Excel',
    exporting: 'جارٍ التحضير…',
    import: 'استيراد Excel',
    importTitle: 'استيراد الموظفين من Excel',
    importSitesTitle: 'استيراد المواقع من Excel',
    importVehiclesTitle: 'استيراد المركبات من Excel',
    newSite: 'موقع جديد',
    newVehicle: 'مركبة جديدة',
    importCompanyTitle: 'استيراد مستندات الشركة من Excel',
    newDocument: 'مستند جديد',
    importHint:
      'استخدم الملف الناتج عن "تصدير Excel". سترى كل تغيير قبل حفظ أي شيء. الخلايا الفارغة تترك البيانات كما هي؛ والعلامة "-" تمسح القيمة.',
    drop: 'اسحب ملف Excel المعدَّل إلى هنا، أو انقر لاختياره',
    checking: 'جارٍ قراءة الملف…',
    updates: '{count} للتحديث',
    creates: '{count} جديد',
    unchanged: '{count} دون تغيير',
    errors: '{count} بها أخطاء (ستُتجاوز)',
    done: 'تم الحفظ: {count} موظفين',
    apply: 'تطبيق {count} تغييرات',
    another: 'اختيار ملف آخر',
    row: 'الصف {row}',
    new: 'موظف جديد',
    skipped: 'سيُتجاوز',
    changed: '{count} تغييرات',
    empty: '(فارغ)',
    nothing: 'لا شيء لتغييره: الملف يطابق ما في البوابة.',
  },

  bulk: {
    button: 'رفع المجلدات',
    title: 'رفع المجلدات',
    hint: 'أفلت مجلدًا لكل شخص باسمه (مثل "Mario Rossi"). يُطابَق كل ملف مع مستنده باسمه. تراجع كل شيء قبل رفع أي شيء.',
    drop: 'اسحب المجلدات إلى هنا',
    dropMore: 'اسحب مجلدات أخرى إلى هنا',
    dropExample: 'مثال: مجلد "Mario Rossi" فيه "carta identità.pdf" و"codice fiscale.pdf" و"contratto.pdf"',
    pick: 'اختيار مجلد',
    pickNote: 'يطلب Chrome التأكيد عند اختيار مجلد؛ ولا يفعل ذلك عند السحب والإفلات.',
    pickFiles: 'اختيار ملفات مفردة',
    photoNeedsImage: 'الصورة (صور JPG أو PNG فقط)',
    looseFiles: 'ملفات بلا مجلد',
    choosePerson: 'اختر الشخص',
    person: 'الشخص صاحب المجلد {folder}',
    noMatch: 'غير موجود: اختر لمن يعود',
    matched: 'تم التعرف عليه من اسم المجلد',
    chosen: 'اختير يدويًا',
    removeFolder: 'إزالة',
    replaces: 'يستبدل المحفوظ (ينتهي في {date})',
    replacesUndated: 'يستبدل المحفوظ',
    typeFor: 'مستند {name}',
    skip: '- عدم الرفع -',
    expiryFor: 'تاريخ انتهاء {name}',
    expiry: 'تاريخ الانتهاء',
    photo: 'الصورة (الملف الشخصي وبطاقة التعريف)',
    sameDates: 'التواريخ نفسها كالملف الأول',
    upload: 'رفع {count} ملفات',
    uploading: 'جارٍ رفع {done} من {total}…',
    needPerson: '{count} مجلدات بلا شخص',
    needDates: '{count} تواريخ انتهاء ناقصة',
    done: 'تم رفع {count} ملفات',
    failedCount: 'فشل {count}: اطلع على السبب ثم حاول مرة أخرى',
    notAccepted: 'نوع الملف هذا غير مقبول.',
    tooBig: 'كبير جدًا ({size}).',
  },

  badge: {
    title: 'بطاقة التعريف',
    needs: 'لإنشائها، أضف: {fields}.',
    ready: 'جاهزة للطباعة، مع الصورة المحفوظة.',
    readyNoPhoto: 'جاهزة: اختر الصورة عند إنشائها.',
    generate: 'إنشاء البطاقة',
    dialogTitle: 'بطاقة التعريف - {name}',
    hint: 'تحقق من الصورة ثم أنشئ البطاقة: ملف PDF للطباعة والقص والتغليف.',
    photoProfile: 'صورة الملف الشخصي',
    photoFound: 'وُجدت بين المستندات: {name}',
    photoChosen: 'الصورة التي اخترتها',
    noPhoto: 'لم تُوجد صورة: اختر واحدة من حاسوبك.',
    unusable: 'صورة الملف الشخصي بصيغة لا تستطيع البطاقة استخدامها: اختر أخرى.',
    choose: 'اختيار صورة أخرى',
    done: 'تم: أنشئ البطاقة',
    generating: 'جارٍ الإنشاء…',
    downloaded: 'تم إنشاء البطاقة: {name}',
    missing: 'ما زال ناقصًا: {fields}. أكمل البيانات الشخصية أولًا.',
  },

  renameAll: {
    button: 'إعادة تسمية كل الملفات',
    title: 'إعادة تسمية كل الملفات',
    hint: 'يمنح كل ملف محفوظ الاسم التلقائي: الرمز والاسم وتاريخ الانتهاء. الملفات الجديدة تُسمّى بهذه الطريقة بالفعل. تبقى الأسماء القديمة في سجل النشاط.',
    count: 'ستُعاد تسمية {count} ملفات. على سبيل المثال:',
    more: 'و{count} أخرى',
    apply: 'إعادة تسمية {count} ملفات',
    nothing: 'كل الملفات لها الاسم التلقائي بالفعل.',
    done: 'تم: أُعيدت تسمية {count} ملفات.',
  },

  compliance: {
    title: 'المستندات وتواريخ الانتهاء',
    subtitle: 'مستندات الموظفين والشركة، مع التجديدات المستحقة',
    missingDocuments: 'المستندات الناقصة',
    expired: 'المنتهية',
    expiring: 'قاربت على الانتهاء',
    companyDocuments: 'مستندات الشركة',
    toHandle: '{count} للمعالجة',
    toHandleShort: 'للمعالجة',
    completeness: 'الاكتمال',
    missing: 'ناقص',
    openFile: 'فتح الملف',
    closeFile: 'إغلاق',
    fileFor: 'ملف — {name}',
    fileSummary: '{missing} ناقص · {expired} منتهٍ · {expiring} ينتهي قريبًا',
    document: 'المستند',
    reference: 'المرجع',
    issued: 'الإصدار',
    expiry: 'الانتهاء',
    register: 'تسجيل',
    renew: 'تجديد',
    recordTitle: 'تسجيل — {label}',
    renewTitle: 'تجديد — {label}',
    recordExplainer:
      'يُؤرشف المستند السابق ولا يُكتب فوقه: "ماذا كان محفوظًا لدينا في 12 مارس" سؤال يُطرح عاجلًا أو آجلًا.',
    filename: 'اسم الملف',
    referenceNumber: 'الرقم / البروتوكول',
    issuedAt: 'تاريخ الإصدار',
    expiryRequired: 'مطلوب لهذا المستند.',
    mandatory: 'إلزامي',
  },

  alerts: {
    title: 'تواريخ الانتهاء والتنبيهات',
    subtitle: 'تُنشأ تلقائيًا كل ليلة من المستندات والشهادات',
    rescan: 'إعادة الفحص الآن',
    rescanning: 'جارٍ الفحص…',
    critical: 'حرجة',
    criticalHint: 'منتهية أو خلال 14 يومًا',
    warning: 'تحذير',
    warningHint: 'خلال 30 يومًا، أو ناقصة',
    info: 'للعلم',
    showAcknowledged: 'عرض ما تم تسلّمه أيضًا',
    countOne: 'تنبيه واحد',
    countMany: '{count} تنبيهات',
    severity: 'الخطورة',
    searchPlaceholder: 'الموظف، المستند…',
    alert: 'التنبيه',
    concerns: 'يخص',
    kind: 'النوع',
    dueOn: 'الاستحقاق',
    detectedAt: 'تاريخ الرصد',
    acknowledge: 'تسلّم هذا التنبيه',
    acknowledged: 'تم تسلّمه',
    acknowledgedBy: 'تسلّمه {name} — {note}',
    acknowledgeExplainer:
      'يبقى التنبيه مفتوحًا حتى يُجدَّد المستند، لكنه يتوقف عن الظهور بين ما يجب معالجته.',
    notePlaceholder: 'مثال: طُلب التجديد في 21/09، ومتوقع بحلول الخميس',
    noneOpen: 'لا توجد تواريخ انتهاء مفتوحة. كل المستندات سليمة.',
    noneToHandle: 'لا شيء للمعالجة.',
  },

  calendar: {
    title: 'التقويم',
    subtitle: 'المواعيد والمهل والغيابات في مكان واحد.',
    today: 'اليوم',
    previous: 'الشهر السابق',
    next: 'الشهر التالي',
    add: 'موعد جديد',
    addForDay: 'إضافة',
    allDay: 'طوال اليوم',
    until: 'حتى {date}',
    nothing: 'لا شيء مخطط في هذا اليوم.',
    more: '+{count}',
    itemsOne: 'عنصر واحد',
    itemsMany: '{count} عناصر',
    kind: {
      appointment: 'موعد',
      deadline: 'مهلة',
      leave: 'غياب',
    },
    source: {
      document: 'مستند',
      job: 'انتهاء عمل',
      equipment: 'معدات مستحقة الإرجاع',
      vehicle: 'مركبة مستحقة الإرجاع',
      approved: 'تمت الموافقة',
      pending: 'بانتظار الموافقة',
    },
    attendees: 'الحاضرون',
    everyone: 'ظاهر للجميع',
    open: 'فتح',
    editTitle: 'تعديل الموعد',
    newTitle: 'موعد جديد',
    fieldTitle: 'العنوان',
    place: 'المكان',
    startDate: 'البداية',
    endDate: 'النهاية (اختياري)',
    startTime: 'وقت البدء',
    endTime: 'وقت الانتهاء (اختياري)',
    site: 'الموقع',
    job: 'العمل',
    noSite: 'بلا موقع',
    noJob: 'بلا عمل',
    attendeesField: 'من سيحضر',
    attendeesHint: 'إذا لم تختر أحدًا يكون الموعد ظاهرًا للجميع.',
    attendeesSelf: 'لا يراه إلا أنت والمكتب.',
    endBeforeStart: 'النهاية قبل البداية.',
    deleteConfirm: 'حذف الموعد {title}؟',
    deleteConfirmLabel: 'حذف الموعد',
    legend: 'دليل الألوان',
  },

  leave: {
    title: 'الإجازات والأذونات',
    subtitle: 'طلبات الموظفين: يوافق عليها المكتب أو يرفضها.',
    tabs: {
      pending: 'للمعالجة',
      approved: 'موافق عليها',
      rejected: 'مرفوضة',
      all: 'الكل',
    },
    status: {
      pending: 'بالانتظار',
      approved: 'موافق عليه',
      rejected: 'مرفوض',
      cancelled: 'ملغى',
    },
    person: 'الموظف',
    type: 'النوع',
    period: 'الفترة',
    note: 'ملاحظة الموظف',
    decision: 'القرار',
    requested: 'تاريخ الطلب',
    hoursRange: 'من {from} إلى {to}',
    approve: 'موافقة',
    reject: 'رفض',
    searchPlaceholder: 'الموظف، النوع، الملاحظات…',
    countOne: 'طلب واحد',
    countMany: '{count} طلبات',
    empty: 'لا توجد طلبات في هذا العرض.',
    cannotDecide: 'لا يمكنك الموافقة أو الرفض: بحسب الإعدادات، المسؤول وحده يبتّ في الطلبات.',
    officeOnly: 'هذه الصفحة للمكتب. تُرسل طلبات الإجازات والأذونات من التطبيق.',
    disabled: 'طلبات التطبيق متوقفة في الإعدادات. تبقى الطلبات المرسلة سابقًا هنا.',
    decidedBy: 'بواسطة {name}',
    approveTitle: 'الموافقة على الطلب',
    rejectTitle: 'رفض الطلب',
    dialogHint: 'يصل الموظف إشعار بردّك.',
    noteLabel: 'ملاحظة للموظف (اختياري)',
    notePlaceholder: 'مثال: تمت الموافقة، أو: عدد كبير من الزملاء غائبون في تلك الأيام',
  },

  audit: {
    adminOnly: 'للمسؤولين فقط',
    adminOnlyHint:
      'يسجّل سجل النشاط ما يفعله كل من في المكتب، بمن فيهم المديرون، ولذلك لا يقرؤه إلا المسؤول — وهذا ما يجعله دليلًا. اطلب من صاحب حساب المسؤول.',
    title: 'سجل النشاط',
    subtitle: 'للإضافة فقط. لا يمكن حذف شيء من هنا.',
    actor: 'المستخدم',
    action: 'الإجراء',
    entity: 'العنصر',
    when: 'الوقت',
    immutableNotice:
      'لا يمكن تعديل هذا السجل أو حذفه. إنه الدليل الذي يحمي الشركة والموظف معًا عند النزاع على وردية.',
    system: 'النظام',
    searchPlaceholder: 'المستخدم، الإجراء، العنصر…',
    countOne: 'سجل واحد',
    countMany: '{count} سجلات',
    actions: {
      user_created: 'إنشاء مستخدم',
      user_updated: 'تحديث مستخدم',
      user_exported: 'تصدير الموظفين إلى Excel',
      user_reinvited: 'إعادة إرسال الدعوة',
      user_login_created: 'إنشاء دخول بالاسم',
      user_password_reset: 'إعادة تعيين كلمة المرور',
      user_suspended: 'إيقاف مستخدم',
      user_reactivated: 'إعادة تفعيل مستخدم',
      shift_clock_in: 'تسجيل الدخول للعمل',
      shift_clock_out: 'تسجيل الخروج من العمل',
      shift_adjusted: 'تعديل وردية',
      shift_auto_closed: 'إغلاق وردية تلقائيًا',
      job_created: 'إنشاء عمل',
      job_status_changed: 'تغيير حالة عمل',
      job_assigned: 'تكليف بعمل',
      job_unassigned: 'إزالة تكليف',
      document_type_created: 'إنشاء مجلد',
      document_type_updated: 'تغيير مجلد',
      document_type_deleted: 'حذف مجلد',
      document_uploaded: 'رفع مستند',
      vehicle_created: 'إضافة مركبة',
      vehicle_updated: 'تغيير مركبة',
      vehicle_assigned: 'تكليف مركبة',
      vehicle_returned: 'إعادة مركبة',
      vehicle_deleted: 'حذف مركبة',
      document_exempted: 'تعيين مستند كغير مطلوب',
      document_exemption_removed: 'جعل المستند مطلوبًا من جديد',
      document_sent: 'إرسال مستند بالبريد الإلكتروني',
      document_bulk_renamed: 'إعادة تسمية الملفات تلقائيًا',
      document_viewed: 'معاينة مستند',
      document_downloaded: 'تنزيل مستند',
      document_deleted: 'حذف مستند',
      site_created: 'إنشاء موقع',
      site_updated: 'تحديث موقع',
      settings_changed: 'تغيير الإعدادات',
      assignment_created: 'إنشاء تكليف',
      assignment_removed: 'إزالة تكليف',
      alert_acknowledged: 'تسلّم تنبيه',
      document_recorded: 'تسجيل مستند',
      calendar_event_created: 'إنشاء موعد',
      calendar_event_updated: 'تغيير موعد',
      calendar_event_deleted: 'حذف موعد',
      leave_requested: 'طلب إجازة أو إذن',
      leave_cancelled: 'إلغاء طلب',
      leave_approved: 'الموافقة على طلب',
      leave_rejected: 'رفض طلب',
    },
  },

  inventory: {
    descriptionPlaceholder: 'ما هو، وأي شيء يستحق الذكر عن هذه القطعة.',
    purchasePriceHint: 'لا يراه إلا المكتب.',
    moreDetails: 'مزيد من التفاصيل',
    moreDetailsHint: 'الفئة، الصانع، الشراء',
    delete: 'حذف القطعة',
    deleteTitle: 'حذف هذه القطعة؟',
    deleteConfirm:
      '{code} — ينتقل {name} إلى سلة المحذوفات ويمكن استعادته خلال 30 يومًا.',
    deleteOutWarning:
      'هذه القطعة مُسلَّمة إلى {holder}. سجّل إعادتها أولًا — فحذفها الآن يضيّع السجل الوحيد لمن يحملها.',
    statusHint_available: 'في المخزن، جاهزة للتسليم.',
    statusHint_assigned: 'عند شخص ما. تُضبط بالتسليم، لا يدويًا.',
    statusHint_maintenance: 'عادت لكنها غير صالحة للاستخدام — في الورشة، أو للمعايرة، أو بانتظار قطعة. تبقى في السجل وخارج قائمة التسليم.',
    statusHint_lost: 'لن تعود، على حد علم الجميع.',
    statusHint_retired: 'أُتلفت أو بيعت. تبقى للسجل التاريخي لا للاستخدام.',
    statusMeaning: 'معنى الحالات',
    scan: 'مسح',
    scanTitle: 'مسح ملصق',
    scanHint: 'وجّه الكاميرا إلى رمز QR أو الباركود الموجود على الأداة.',
    scanStart: 'تشغيل الكاميرا',
    scanStop: 'إيقاف',
    scanAgain: 'مسح آخر',
    scanNoCamera: 'لا توجد كاميرا متاحة',
    scanNoCameraHint:
      'لا توجد كاميرا في هذا الجهاز، أو لا يسمح المتصفح بالوصول إليها. لا يزال بإمكانك كتابة الرمز.',
    scanDenied: 'الكاميرا محظورة',
    scanDeniedHint:
      'اسمح بالوصول إلى الكاميرا لهذا الموقع من إعدادات المتصفح ثم حاول مرة أخرى.',
    scanInsecure: 'الكاميرا تحتاج إلى اتصال آمن',
    scanInsecureHint:
      'لا تمنح المتصفحات الوصول إلى الكاميرا إلا عبر HTTPS. سيعمل ذلك عند نشر البوابة؛ وعلى هذا الجهاز يعمل على localhost.',
    scanManual: 'أو اكتب الرمز',
    scanManualAction: 'بحث',
    scanLooking: 'جارٍ البحث…',
    scanFound: 'تم العثور عليها',
    handOver: 'تسليم',
    handBack: 'استلام',
    scanReady: 'في المخزن',
    scanReadyHint: 'لا أحد يحملها. سلّمها لشخص ما أدناه.',
    scanOut: 'عند {holder}',
    scanOutHint: 'منذ {date}. استلمها أدناه.',
    installHint:
      'أضف البوابة إلى الشاشة الرئيسية فيفتح الماسح بلمسة واحدة، دون شريط العنوان.',
    category_power_tools: 'أدوات كهربائية',
    category_measuring: 'أجهزة القياس',
    category_ppe: 'معدات الوقاية الشخصية',
    category_access: 'السلالم والأبراج',
    category_vehicles: 'المركبات',
    category_other: 'أخرى',
    allCategories: 'كل الفئات',
    allStatuses: 'كل الحالات',
    photoOf: 'صورة {name}',
    noPhoto: 'لا توجد صورة',
    selectRow: 'تحديد {name}',
    selectAll: 'تحديد كل المعروض',
    selectedOne: 'تم تحديد {count}',
    selectedMany: 'تم تحديد {count}',
    clearSelection: 'مسح التحديد',
    printSelected: 'طباعة {count} ملصقات',
    printAll: 'طباعة كل الملصقات',
    printSheetSelected: 'ملصقات القطع الـ {count} التي حددتها.',
    printSheetAll: 'ملصقات كل قطعة في المخزون.',
    printSaveHint:
      'يمكن لنافذة الطباعة في متصفحك حفظ هذا بصيغة PDF بدل طباعته — اختر “حفظ بصيغة PDF” وجهةً.',
    sectionItem: 'ما هي',
    sectionItemHint: 'الاسم هو ما سيبحث عنه الناس.',
    sectionPurchase: 'الشراء',
    sectionPurchaseHint: 'اختياري، ولا يراه إلا المكتب.',
    sectionLabel: 'الملصق',
    sectionPhoto: 'الصورة',
    removePhoto: 'إزالة الصورة',
    resolving: 'جارٍ البحث عن القطعة…',
    scanFailed: 'لم يتم العثور على القطعة',
    scanFailedHint: 'لا توجد قطعة تطابق الرمز “{code}”.',
    title: 'المخزون',
    subtitle: 'الأدوات والمعدات، ومن يحملها الآن.',
    searchPlaceholder: 'الرمز، الاسم، الرقم التسلسلي، من يحملها…',
    countOne: 'قطعة واحدة',
    countMany: '{count} قطع',
    addItem: 'إضافة قطعة',
    addItemHint:
      'يُنشأ الرمز لك ويوضع على الملصق. الرقم التسلسلي للصانع حقل منفصل.',
    item: 'القطعة',
    code: 'الرمز',
    category: 'الفئة',
    serialNumber: 'الرقم التسلسلي',
    serialHint: 'رقم الصانع — وهو الرقم الذي تطلبه شركة التأمين أو محضر الشرطة.',
    manufacturer: 'الصانع',
    model: 'الطراز',
    purchasedOn: 'تاريخ الشراء',
    purchasePrice: 'سعر الشراء',
    description: 'الوصف',
    namePlaceholder: 'مثال: مثقاب Hilti TE 30',
    photo: 'الصورة',
    photoHint: 'مفيدة للتعرّف عليها عند عودتها.',
    photoLabel: 'صورة المخزون',

    holder: 'بحوزة',
    holderPerson: 'الموظف',
    holderSite: 'الموقع',
    inStore: 'في المخزن',
    dueBack: 'موعد الإعادة',
    dueBackHint: 'اتركه فارغًا إن لم يكن هناك موعد إعادة.',
    checkedOutAt: 'تاريخ التسليم',
    overdue: 'متأخرة',
    overdueBy: 'متأخرة {count} يوم',
    overdueBanner: '{count} قطع تجاوزت موعد إعادتها. وهي في أعلى القائمة.',

    status_available: 'متاحة',
    status_assigned: 'مُسلَّمة',
    status_maintenance: 'في الصيانة',
    status_lost: 'مفقودة',
    status_retired: 'خارج الخدمة',

    checkOut: 'تسليم',
    checkOutTitle: 'تسليم “{name}”',
    checkOutHint: 'تبقى مُسلَّمة حتى تسجّل إعادتها.',
    checkIn: 'تسجيل الإعادة',
    checkInTitle: 'إعادة “{name}”',
    giveTo: 'إلى من',
    giveToHint: 'موظف، أو موقع إن بقيت الأداة هناك.',
    conditionOut: 'ملاحظات التسليم',
    conditionHint: 'الحالة، وما هو مشمول، وما يجب أن يعود.',
    conditionIn: 'ملاحظات الإعادة',
    backAs: 'الحالة عند الإعادة',
    backAsHint: 'اختر “في الصيانة” إن عادت لإصلاحها.',

    history: 'الحركات',
    historyHint:
      'كل تسليم وكل إعادة. لا تُعدَّل هذه السجلات أبدًا — فهي الجواب عن سؤال “من كانت عنده في مارس”.',
    neverIssued: 'لم تُسلَّم هذه القطعة لأحد من قبل.',
    stillOut: 'لا تزال خارج المخزن',
    outOn: 'سُلِّمت في {date}',
    backOn: 'عادت في {date}',
    backToList: 'العودة إلى المخزون',

    label: 'الملصق',
    labelHint:
      'اطبعه والصقه على الأداة. توجيه الكاميرا إلى الرمز يفتح هذه الصفحة.',
    labelKind: 'نوع الملصق',
    labelKindHint: 'اختر الباركود إن كانت الأداة تحمل واحدًا بالفعل.',
    qr: 'رمز QR',
    barcode: 'باركود',
    externalCode: 'رمز موجود',
    externalCodeHint: 'إن كان على الأداة ملصق من الصانع، فمسحه سيجد هذه القطعة أيضًا.',
    print: 'طباعة',
    printLabels: 'طباعة الملصقات',
    printLabelsHint: 'ورقة واحدة بكل الملصقات، للقص واللصق.',
  },

  recycle: {
    inventory: 'قطعة',
    title: 'سلة المحذوفات',
    subtitle:
      'كل ما حُذف. يمكن استعادته خلال {count} يومًا، ثم يُزال نهائيًا.',
    subtitleShort: 'عناصر محذوفة، يمكن استعادتها لفترة محدودة.',
    searchPlaceholder: 'الاسم، النوع، من حذفه…',
    item: 'العنصر',
    kind: 'النوع',
    client: 'العميل',
    document: 'المستند',
    deletedAt: 'تاريخ الحذف',
    daysLeft: 'الأيام المتبقية',
    daysLeftValue: '{count} يوم',
    restore: 'استعادة',
    purge: 'حذف نهائي',
    purgeTitle: 'حذف نهائي؟',
    purgeConfirm: 'سيُزال “{name}” الآن ولا يمكن استرجاعه.',
    countOne: 'عنصر واحد',
    countMany: '{count} عناصر',
  },

  settings: {
    courses: {
      title: 'الدورات والرخص',
      hide: 'إخفاء مستندات الدورات والرخص عن الموظفين',
      hideHint:
        'لا يستطيع الموظفون فتح أو تنزيل المستندات الموجودة في مجلدات الدورات والرخص من التطبيق. يراها المكتب دائمًا.',
    },
    calLeave: {
      title: 'التقويم والإجازات',
      hint: 'ما يراه الموظفون وما يستطيعون فعله من التطبيق.',
      calendarInApp: 'إظهار التقويم في التطبيق',
      calendarInAppHint: 'تظهر المواعيد والمهل والغيابات في تطبيق الموظفين.',
      employeesCanAdd: 'يمكن للموظفين إضافة مواعيد',
      employeesCanAddHint: 'الموعد الذي يضيفه موظف لا يراه إلا هو والمكتب.',
      leaveEnabled: 'طلبات الإجازات والأذونات مفعّلة',
      leaveEnabledHint: 'يستطيع الموظفون طلب إجازات وأذونات من التطبيق. تبقى الطلبات المرسلة ظاهرة.',
      managersCanApprove: 'يمكن للمديرين الموافقة',
      managersCanApproveHint: 'عند إيقافه، يوافق المسؤول وحده على الطلبات أو يرفضها.',
      typesTitle: 'أنواع الطلبات',
      typesHint: 'ما يستطيع الموظفون طلبه. يمكن تغيير الاسم في أي وقت؛ ويبقى الرمز الداخلي كما أُعطي عند الإنشاء.',
      typeIt: 'الاسم بالإيطالية',
      typeEn: 'الاسم بالإنجليزية',
      typeHours: 'بالساعات',
      typeHoursHint: 'يوم واحد فقط، بوقت بدء ووقت انتهاء.',
      typeAdd: 'إضافة نوع',
      typeRemove: 'إزالة النوع',
      typeKeepOne: 'يجب أن يبقى نوع واحد على الأقل.',
      typeNameRequired: 'لكل نوع اسم بالإيطالية.',
      typeRemovedNote: 'تبقى الطلبات المرسلة بنوع أُزيل في القائمة، ويظهر الرمز بدل الاسم.',
      newType: 'نوع جديد',
    },
    auditNote: 'كل تغيير يجريه أي شخص يُسجَّل ولا يمكن تعديله أو حذفه. يستطيع المسؤولون الاطلاع عليه هنا:',
    allowEarly: 'السماح بتسجيل الدخول للعمل قبل البداية المخططة',
    allowEarlyHint:
      'يُنصح به. الوصول مبكرًا والتحضير أمر معتاد في الموقع، ويُبلَّغ المكتب بتسجيل الدخول المبكر في الحالتين.',
    allowLate: 'السماح بتسجيل الخروج من العمل بعد النهاية المخططة',
    allowLateHint:
      'إن أوقفته فلن يستطيع من ينتهي متأخرًا تسجيل الخروج إطلاقًا — وستُغلق ورديته تلقائيًا خلال الليل، وهذا أسوأ من علامة. هذا الخيار هو الذي يضخّم الفاتورة، لذا قراءة العلامات أفضل من الحظر.',
    graceLabel: 'مهلة على الوقت المخطط (بالدقائق)',
    graceHint:
      'خلال هذا العدد من الدقائق يُحتسب تسجيل الدخول للعمل في الوقت المحدد. وبدون مهلة، يُعلَّم كل صباح من يسجّل الدخول في 08:57 لعمل يبدأ في 09:00.',
    workingHours: 'ساعات العمل',
    perJobNote: 'يمكن أن يحمل كل عمل قواعده الخاصة — وهي في صفحة العمل نفسه.',
    theme: 'المظهر',
    language: 'اللغة',
    perSiteNote:
      'تنطبق هذه على كل المواقع. ويمكن أن يحمل كل موقع هوامشه الخاصة — وهي في صفحة الموقع نفسه، تحت الخريطة.',
    openSites: 'الانتقال إلى المواقع',
    location: 'الموقع الجغرافي',
    companyTitle: 'بيانات الشركة على بطاقة التعريف',
    companyHint: 'تُطبع أعلى كل بطاقة تعريف. غيّرها هنا إن تغيرت.',
    companyName: 'اسم الشركة',
    companyVat: 'الرمز الضريبي / رقم ضريبة القيمة المضافة',
    companyAddress: 'العنوان المسجّل',
    companyEmployer: 'صاحب العمل (الممثل القانوني)',
    saveSettings: 'حفظ الإعدادات',
    radiusLabel: 'نصف قطر النطاق الافتراضي (م)',
    radiusHint: 'يُستخدم للمواقع الجديدة. ويمكن لكل موقع تجاوزه.',
    accuracyTrustLabel: 'الدقة الموثوقة (م)',
    accuracyTrustHint: 'دون هذا الرقم يُعدّ تحديد GPS موثوقًا.',
    accuracyRejectLabel: 'الدقة غير الصالحة (م)',
    accuracyRejectHint:
      'فوق هذا الرقم يُحتسب التحديد “بلا GPS” بدل “خارج النطاق”.',
    maxShiftLabel: 'الإغلاق التلقائي بعد (ساعات)',
    maxShiftHint: 'الوردية المتروكة مفتوحة بعد هذا الحد تُغلق وتُعلَّم للمراجعة.',
    offlineAgeLabel: 'أقصى عمر لتسجيلات الدخول للعمل دون اتصال (ساعات)',
    offlineAgeHint: 'بعد هذا الحد يحتاج التسجيل إلى مراجعة يدوية. ولا يُهمل أبدًا.',

    locationNote:
      'لا يُسجَّل الموقع إلا عند تسجيل الدخول للعمل وتسجيل الخروج منه، ولا يُسجَّل أبدًا أثناء الوردية.',
    title: 'الإعدادات',
    subtitle: 'حسابك، وقواعد الشركة، ومن يستطيع استخدام البوابة',
    appearance: 'المظهر',
    appearanceHint: 'ينطبق على هذا المتصفح فقط.',
    clockIn: 'تسجيل الدخول للعمل',
    allowOutside: 'السماح بتسجيل الدخول للعمل خارج النطاق (يلزم ذكر السبب)',
    allowOutsideHint:
      'يُنصح به. إن أوقفته فلن يستطيع الموظف الذي لا يقدر على تسجيل الدخول للعمل أن يبدأ عمله — وينتهي المكتب بتصحيح الورديات يدويًا، وهذا أسوأ للجميع من علامة صادقة.',
    saved: 'تم حفظ الإعدادات.',
  },

  alertText: {
    withHolder: '{holder} — {label}',
    expired: '{subject} منتهٍ',
    expiring: '{subject} قارب على الانتهاء',
    expiresToday: '{subject} ينتهي اليوم',
    missing: '{subject} ناقص',
    missingBody: 'مستند إلزامي غير محفوظ.',
    lastDay: 'آخر يوم صلاحية.',
    expiredYesterday: 'انتهى أمس. يجب تجديده قبل الزيارة القادمة للموقع.',
    expiredDaysAgo:
      'انتهى منذ {count} يوم. يجب تجديده قبل الزيارة القادمة للموقع.',
    expiresTomorrow: 'ينتهي غدًا.',
    expiresInDays: 'ينتهي خلال {count} يوم. من الأفضل بدء التجديد الآن.',
  },

  status: {
    job: {
      draft: 'مسودة',
      scheduled: 'مجدول',
      in_progress: 'قيد التنفيذ',
      on_hold: 'معلّق',
      completed: 'مكتمل',
      cancelled: 'ملغى',
    },
    shift: {
      open: 'قيد التنفيذ',
      closed: 'مغلقة',
      auto_closed: 'أُغلقت تلقائيًا',
      disputed: 'محل نزاع',
    },
    user: {
      invited: 'مدعو',
      active: 'نشط',
      suspended: 'موقوف',
      archived: 'مؤرشف',
    },
    role: {
      admin: 'مسؤول',
      manager: 'موظف',
      engineer: 'موظف',
    },
    register: {
      valid: 'ساري',
      expiring: 'قارب على الانتهاء',
      expired: 'منتهٍ',
      missing: 'ناقص',
      not_provided: 'غير مقدَّم',
      exempt: 'غير مطلوب',
    },
    severity: {
      critical: 'حرج',
      warning: 'تحذير',
      info: 'للعلم',
    },
    docCategory: {
      contract: 'عقد',
      drawing: 'مخطط',
      certificate: 'شهادة',
      risk_assessment: 'تقييم المخاطر',
      datasheet: 'ورقة بيانات',
      site_photo: 'صورة الموقع',
      signed_sheet: 'ورقة موقّعة',
      signature: 'توقيع',
      id_document: 'وثيقة هوية',
      other: 'أخرى',
    },
    flag: {
      outside_geofence: 'خارج النطاق',
      mocked_location: 'موقع مزيّف',
      poor_accuracy: 'دقة منخفضة',
      no_fix: 'لا يوجد GPS',
      offline_sync: 'أُرسل دون اتصال',
      auto_closed: 'أُغلقت تلقائيًا',
      manually_adjusted: 'عُدِّلت يدويًا',
      no_job_assigned: 'بلا عمل',
      overlapping_shift: 'ورديات متداخلة',
    },
    alertKind: {
      document_expiring: 'مستند قارب على الانتهاء',
      document_expired: 'مستند منتهي الصلاحية',
      document_missing: 'مستند ناقص',
      certification_expiring: 'شهادة قاربت على الانتهاء',
      certification_expired: 'شهادة منتهية الصلاحية',
      shift_exception: 'استثناء في وردية',
      unassigned_worker: 'موظف غير مكلّف',
    },
  },

  auth: {
    title: 'تسجيل الدخول إلى الحساب',
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    signIn: 'تسجيل الدخول إلى الحساب',
    signingIn: 'جارٍ تسجيل الدخول إلى الحساب…',
    signOut: 'تسجيل الخروج من الحساب',
    forgot: 'هل نسيت كلمة المرور؟',
    back: 'رجوع',
    showPassword: 'إظهار كلمة المرور',
    hidePassword: 'إخفاء كلمة المرور',

    mfa: {
      title: 'التحقق بخطوتين',
      subtitle: 'أدخل الرمز المكوّن من 6 أرقام من تطبيق المصادقة.',
      subtitleEmail: 'أرسلنا رمزًا من 6 أرقام إلى بريدك الإلكتروني.',
      code: 'رمز التحقق',
      verify: 'تحقق',
      resend: 'إرسال مرة أخرى',
      resent: 'أُعيد إرسال الرمز.',
    },

    setupMfa: {
      title: 'تفعيل التحقق بخطوتين',
      subtitle:
        'امسح هذا الرمز بتطبيق Google Authenticator أو Microsoft Authenticator أو 1Password، ثم أدخل الرمز المكوّن من 6 أرقام الذي يعرضه.',
      manual: 'أو أدخل هذا المفتاح يدويًا:',
      copied: 'تم نسخ المفتاح.',
      confirm: 'تفعيل',
      required: 'دورك يتطلب التحقق بخطوتين.',
      skip: 'تخطّي الآن',
    },

    newPassword: {
      title: 'اختر كلمة مرور جديدة',
      subtitle: 'هذا أول تسجيل دخول لك إلى الحساب. تتوقف كلمة المرور المؤقتة عن العمل بعد هذه الخطوة.',
      password: 'كلمة المرور الجديدة',
      confirm: 'تأكيد كلمة المرور',
      submit: 'حفظ وتسجيل الدخول إلى الحساب',
      mismatch: 'كلمتا المرور غير متطابقتين.',
      rules: '12 حرفًا على الأقل، مع حرف كبير وحرف صغير ورقم.',
    },

    forgotFlow: {
      invitationSent: 'لم تحدد كلمة مرور بعد، لذلك أرسلنا إليك دعوة جديدة بكلمة مرور مؤقتة. تحقق من بريدك الإلكتروني.',
      title: 'إعادة تعيين كلمة المرور',
      subtitle: 'أدخل بريدك الإلكتروني. إن وُجد حساب فستصلك رمز من 6 أرقام.',
      send: 'إرسال الرمز',
      sent: 'إن كان هذا العنوان مسجّلًا فالرمز في طريقه إليك.',
      codeTitle: 'أدخل الرمز',
      codeSubtitle: 'تحقق من بريدك الإلكتروني ثم اختر كلمة مرور جديدة.',
      code: 'الرمز',
      newPassword: 'كلمة المرور الجديدة',
      confirm: 'تأكيد كلمة المرور',
      submit: 'إعادة تعيين كلمة المرور',
      done: 'تم تحديث كلمة المرور. يمكنك تسجيل الدخول إلى الحساب الآن.',
    },

    remember: {
      label: 'تذكّرني على هذا الجهاز',
      welcomeBack: 'مرحبًا بعودتك',
      notYou: 'لست أنت؟ استخدم حسابًا آخر',
      orPassword: 'أو بكلمة المرور',
    },
    passkey: {
      signIn: 'تسجيل الدخول إلى الحساب بمفتاح المرور',
      or: 'أو',
      needEmail: 'أدخل بريدك الإلكتروني أولًا ثم استخدم مفتاح المرور.',
    },

    secure: {
      title: 'أمّن حسابك',
      subtitle: 'أضف طبقة ثانية من الأمان. يمكنك فعل ذلك لاحقًا أيضًا من الإعدادات ← الحساب.',
      passkey: 'إضافة مفتاح مرور',
      passkeyHint: 'سجّل الدخول إلى الحساب ببصمتك أو وجهك أو رقم PIN للجهاز. بلا رموز للكتابة.',
      recommended: 'موصى به',
      app: 'تطبيق المصادقة',
      appHint: 'رمز من 6 أرقام من Google Authenticator أو Microsoft Authenticator أو 1Password.',
      skip: 'تخطّي الآن',
    },

    session: {
      expiring: 'جلستك على وشك الانتهاء',
      expiringBody: 'ستُسجَّل خارج الحساب بعد {seconds} ثانية بسبب عدم النشاط.',
      stay: 'البقاء في الحساب',
      timedOut: 'انتهت الجلسة بعد فترة من عدم النشاط. يرجى تسجيل الدخول إلى الحساب مرة أخرى.',
      maxReached: 'انتهت الجلسة. حفاظًا على الأمان، تدوم الجلسة 5 ساعات على الأكثر.',
      noPortalAccess: 'هذا الحساب لا يملك صلاحية الوصول إلى البوابة الداخلية. اطلب من المسؤول تفعيلها.',
    },

    error: {
      invalid: 'البريد الإلكتروني أو كلمة المرور غير صحيحة.',
      code: 'هذا الرمز خاطئ أو منتهي الصلاحية.',
      attempts: 'محاولات كثيرة جدًا. حاول مرة أخرى بعد بضع دقائق.',
      disabled: 'هذا الحساب غير نشط. اتصل بالمكتب.',
      password: 'كلمة المرور هذه لا تستوفي الحد الأدنى من المتطلبات.',
      network: 'تعذّر الاتصال بالخادم. تحقق من اتصالك.',
      unknown: 'حدث خطأ ما. يرجى المحاولة مرة أخرى.',
      noPasskey: 'تسجيل الدخول بمفتاح المرور غير متاح لهذا الحساب: لا يوجد مفتاح مرور مسجّل، أو تطبيق المصادقة مفعّل (لا يعملان معًا). سجّل الدخول بكلمة المرور.',
      passkeyCancelled: 'أُلغي مفتاح المرور أو ليس موجودًا على هذا الجهاز.',
      passkeyUnsupported: 'لا يستطيع هذا المتصفح أو الجهاز استخدام مفاتيح المرور لهذه البوابة.',
      passkeyExists: 'لدى هذا الجهاز مفتاح مرور لحسابك بالفعل.',
      notConfigured: 'المصادقة غير مُهيّأة في هذا الإصدار. اتصل بالمسؤول.',
    },
  },

  access: {
    noEmail: 'بلا بريد إلكتروني — لا حساب بعد',
    noEmailHint:
      'بدون بريد إلكتروني يُضاف الشخص دون حساب: يمكنك حفظ مستنداته فورًا. وبعد إضافته يمكنك إنشاء دخول للتطبيق بالاسم الأول واسم العائلة وكلمة مرور مؤقتة، أو إضافة البريد الإلكتروني لاحقًا من صفحته.',
    noAccountYet:
      'لا حساب بعد. أضف بريده الإلكتروني واختر أين سيسجّل الدخول؛ وستصله الدعوة بالبريد الإلكتروني.',
    createAccount: 'إنشاء حساب',
    createAccountHint: 'تُرسل دعوة إلى هذا العنوان.',
    invited: 'مدعو',
    noSignIn: 'بلا دخول',
    delete: 'حذف',
    deleting: 'جارٍ الحذف…',
    deleteConfirm: 'حذف {name} ({email})؟\n\nلن يستطيع تسجيل الدخول إلى الحساب بعد الآن وسيختفي من كل القوائم. تبقى وردياته ومستنداته وسجل النشاط: فهذه سجلات يجب على الشركة الاحتفاظ بها.',
    reinvite: 'إعادة إرسال الدعوة',
    reinviting: 'جارٍ الإرسال…',
    reinviteDone: 'أُرسلت الدعوة',
    reinviteSent: 'أُرسلت كلمة مرور مؤقتة جديدة إلى {email}.',
    reinviteHint: 'ترسل كلمة مرور مؤقتة جديدة: تلك الموجودة في الدعوة الأولى تنتهي بعد 7 أيام.',
    nameLogin: {
      heading: 'الدخول إلى التطبيق بالاسم',
      loginName: 'اسم الدخول',
      none: 'لا يوجد دخول بالاسم بعد. أنشئ واحدًا ليتمكن من فتح التطبيق باسمه الأول واسم عائلته وكلمة مرور مؤقتة.',
      typedHint: 'في التطبيق يكتب الاسم الأول "{first}" واسم العائلة "{last}" وكلمة المرور.',
      differentName:
        'اسم الدخول هذا ليس مطابقًا للاسم الأول واسم العائلة: يجب أن يكتب "{loginName}" تمامًا.',
      create: 'إنشاء دخول بالاسم',
      reset: 'إعادة تعيين كلمة المرور',
      createTitle: 'الدخول بالاسم لـ {name}',
      resetTitle: 'إعادة تعيين كلمة مرور {name}',
      resultTitle: 'كلمة المرور المؤقتة لـ {name}',
      createIntro:
        'ينشئ دخولًا للتطبيق بالاسم الأول واسم العائلة وكلمة مرور مؤقتة. وعند أول دخول سيتعين عليه اختيار كلمة مروره بنفسه.',
      resetIntro:
        'ينشئ كلمة مرور مؤقتة جديدة. تتوقف القديمة عن العمل فورًا، وعند الدخول التالي سيتعين عليه اختيار كلمة مروره بنفسه.',
      leavesEmail:
        'يسجّل الدخول اليوم بالبريد الإلكتروني {email}. وبالدخول بالاسم سيسجّل بالاسم الأول واسم العائلة، وستتوقف كلمة المرور القديمة عن العمل.',
      offerAfterAdd:
        'أُضيف {name} بدون بريد إلكتروني. هل تنشئ الآن دخول التطبيق بالاسم الأول واسم العائلة؟ ستحصل على كلمة مرور مؤقتة لتسليمها.',
      notNow: 'ليس الآن',
      generate: 'إنشاء كلمة المرور',
      needName:
        'لا يمكن استنتاج اسم دخول من اسم هذا الشخص. اكتب واحدًا: أحرف لاتينية صغيرة وأرقام ونقاط (مثل mario.rossi).',
      nameInvalid: 'استخدم أحرفًا لاتينية صغيرة بدون علامات وأرقامًا ونقاطًا فقط، مثل mario.rossi.',
      password: 'كلمة المرور المؤقتة',
      copy: 'نسخ',
      copied: 'تم النسخ',
      copyFailed: 'فشل النسخ: حدّد كلمة المرور وانسخها يدويًا.',
      onceWarning:
        'تظهر كلمة المرور هذه الآن فقط: فهي غير محفوظة ولا يمكن إظهارها مرة أخرى. وعند أول دخول سيتعين على الشخص اختيار كلمة مروره بنفسه. وإن فقدتها فأعد تعيينها.',
      instructionLabel: 'التعليمات للتسليم',
      instruction:
        'افتح تطبيق HH Electrical Systems. سجّل الدخول إلى الحساب بـ: {first} {last}، كلمة المرور: {password}.',
      instructionLogin:
        'افتح تطبيق HH Electrical Systems. سجّل الدخول إلى الحساب باسم الدخول: {loginName}، كلمة المرور: {password}.',
      copyInstruction: 'نسخ التعليمات',
      done: 'تم',
    },
    heading: 'الوصول والصلاحيات',
    signsInTo: 'يمكنه الدخول إلى',
    manage: 'إدارة الوصول',
    discard: 'تجاهل التغييرات',
    portalAccess: 'ما يستطيع فعله في البوابة',
    permissionsForNew: 'ما سيستطيع فعله في البوابة',
    summary: '{visible} أقسام ظاهرة · {editable} قابلة للتعديل',
    fullAccess: 'للمسؤولين وصول كامل إلى كل الأقسام.',
    appOnly: 'التطبيق فقط — بلا وصول إلى البوابة. فعّل “البوابة الداخلية” أعلاه لاختيار ما يستطيع رؤيته.',
    setAll: 'الكل:',
    presets: {
      title: 'ابدأ من:',
      viewAll: 'يرى كل شيء',
      editDaily: 'يدير العمل اليومي',
      nothing: 'بلا وصول',
    },
    groups: {
      overview: 'نظرة عامة',
      operations: 'العمليات',
      records: 'السجلات',
      compliance: 'الامتثال والرسائل',
      system: 'النظام',
    },
    tabGeneral: 'عام',
    tabUsers: 'المستخدمون والصلاحيات',
    title: 'مستخدمو البوابة',
    hint: 'لكل شخص دور يحدد صلاحياته الأولية. وهنا يمكنك تغيير ما يستطيع كل شخص رؤيته أو تعديله، قسمًا بقسم.',
    newUser: 'مستخدم جديد',
    newUserHint:
      'عند إدخال بريد إلكتروني تصله دعوة بكلمة مرور مؤقتة. وبدونه يُسجَّل الشخص فقط؛ أنشئ الحساب لاحقًا.',
    firstName: 'الاسم الأول',
    lastName: 'اسم العائلة',
    email: 'البريد الإلكتروني',
    role: 'الدور',
    create: 'إنشاء مستخدم',
    phone: 'الهاتف',
    kind: 'من هو',
    kindAdmin: 'مسؤول',
    kindAdminHint: 'يدير كل شيء، بما في ذلك المستخدمون والصلاحيات. لديه البوابة دائمًا.',
    kindEmployee: 'موظف',
    kindEmployeeHint: 'فني ميداني أو موظف مكتب. ما يستطيع رؤيته يُحدَّد في الصلاحيات.',
    doors: 'الحسابات المراد إنشاؤها',
    doorPortal: 'البوابة الداخلية',
    doorPortalHint: 'يسجّل الدخول من حاسوب: الورديات والمواقع والمستندات، بحسب الصلاحيات.',
    doorApp: 'التطبيق',
    doorAppHint: 'يسجّل الدخول للعمل ويرى أعماله ومستنداته من الهاتف.',
    doorPortalShort: 'البوابة',
    doorAppShort: 'التطبيق',
    noDoorsHint: 'بلا وصول: الشخص مسجّل لكنه لا يستطيع تسجيل الدخول إلى الحساب حتى تفتح له بابًا هنا.',
    created: 'تم إنشاء المستخدم. رسالة البريد الإلكتروني بكلمة المرور المؤقتة في طريقها.',
    permissions: 'الصلاحيات',
    section: 'القسم',
    levelShort: {
      none: 'لا شيء',
      read: 'عرض',
      write: 'تعديل',
    },
    level: {
      none: 'بلا وصول',
      read: 'عرض فقط',
      write: 'عرض وتعديل',
    },
    inherited: 'من الدور',
    save: 'حفظ الصلاحيات',
    saved: 'تم حفظ الصلاحيات.',
    suspend: 'إيقاف',
    reactivate: 'إعادة التفعيل',
    you: 'أنت',
    selfLock: 'لا يمكنك إزالة قدرتك على إدارة المستخدمين.',
    empty: 'لا يوجد مستخدمون بعد. أنشئ الأول.',
    sections: {
      dashboard: 'لوحة المعلومات',
      shifts: 'الورديات',
      payroll: 'الساعات والأجور',
      assignments: 'التكليفات',
      jobs: 'الأعمال',
      employees: 'الموظفون',
      sites: 'المواقع',
      documents: 'المستندات',
      compliance: 'تواريخ الانتهاء والمستندات المطلوبة',
      alerts: 'التنبيهات',
      inventory: 'المخزون',
      vehicles: 'المركبات',
      audit: 'سجل النشاط',
      recycle_bin: 'سلة المحذوفات',
      settings: 'الإعدادات',
      users: 'المستخدمون والصلاحيات',
      messages: 'الرسائل',
      calendar: 'التقويم',
      leave: 'الإجازات والأذونات',
    },
  },

  messages: {
    search: 'ابحث بالاسم أو البريد الإلكتروني أو النص',
    noMatches: 'لا توجد رسائل تطابق هذا البحث.',
    selectOne: 'اختر رسالة لقراءتها.',
    reply: 'رد',
    call: 'اتصال',
    back: 'كل الرسائل',
    email: 'البريد الإلكتروني',
    language: 'اللغة',
    readLabel: 'مقروءة',
    title: 'الرسائل',
    subtitle: 'الاستفسارات الواردة من نموذج التواصل في الموقع',
    inbox: 'الوارد',
    archived: 'المؤرشفة',
    new: 'جديدة',
    noSubject: 'بلا موضوع',
    empty: 'لا توجد رسائل.',
    emptyHint: 'عندما يملأ أحدهم النموذج في الموقع تظهر رسالته هنا.',
    emptyArchived: 'لا توجد رسائل مؤرشفة.',
    readBy: 'قرأها {name} في {when}',
    wroteInItalian: 'كتب بالإيطالية',
    wroteInArabic: 'كتب بالعربية',
    wroteInEnglish: 'كتب بالإنجليزية',
    archive: 'أرشفة',
    restore: 'استعادة',
  },
  nudge: {
    pushTitle: 'تفعيل الإشعارات',
    pushText: 'تذكيرات الأعمال وتواريخ الانتهاء والمستجدات على هذا الجهاز، حتى والتطبيق مغلق.',
    pushOn: 'تفعيل',
    dismiss: 'ليس الآن',
  },
  notifications: {
    tab: 'الإشعارات',
    bell: 'الإشعارات',
    unread: '{count} غير مقروءة',
    markAllRead: 'تحديد الكل كمقروء',
    empty: 'لا توجد إشعارات.',
    emptyHint: 'التذكيرات وتواريخ الانتهاء والمستجدات تظهر هنا.',
    settingsLink: 'إعدادات الإشعارات',
    intro: 'اختر ما تتلقاه، وكيف، وقبل كم من الوقت. ينطبق على البوابة والتطبيق معًا.',
    groups: {
      schedule: 'جدولك',
      documents: 'مستنداتك',
      equipment: 'المعدات التي بحوزتك',
      office: 'للمكتب',
    },
    types: {
      job_start: { title: 'بدء عمل', hint: 'تذكير قبل بدء كل عمل مكلّف به.' },
      clock_out: { title: 'تذكير بتسجيل الخروج', hint: 'إذا كنت لا تزال مسجّلًا بعد موعد انتهاء العمل المخطط.' },
      job_assigned: { title: 'تكليف بعمل جديد', hint: 'عندما يكلّفك المكتب بعمل.' },
      job_changed: { title: 'تغيير موعد عمل أو إلغاؤه', hint: 'عندما يتغير موعد أحد أعمالك أو يُلغى.' },
      shift_adjusted: { title: 'تصحيح وردية من المكتب', hint: 'عندما يغيّر المكتب أوقات إحدى ورديّاتك، مع ذكر السبب.' },
      document_expiring: { title: 'مستند قارب على الانتهاء', hint: 'الشهادات والدورات والمستندات الشخصية، قبل انتهاء صلاحيتها.' },
      document_expired: { title: 'مستند منتهي الصلاحية', hint: 'بعد تاريخ الانتهاء، إذا لم يُجدَّد.' },
      equipment_due: { title: 'معدات مستحقة الإرجاع', hint: 'قبل موعد إرجاع المعدات التي بحوزتك.' },
      equipment_overdue: { title: 'معدات متأخرة', hint: 'عندما يمر موعد الإرجاع.' },
      staff_document_expiring: { title: 'مستندات العاملين قاربت على الانتهاء', hint: 'مستندات كل موظف، قبل انتهاء صلاحيتها.' },
      missed_clock_in: { title: 'تسجيل دخول فائت', hint: 'عندما لا يسجّل المكلّف بعمل دخوله للعمل بعد بدئه.' },
      shift_flagged: { title: 'تسجيل دخول غير نظامي', hint: 'تسجيلات الدخول للعمل خارج الموقع أو خارج الساعات، لحظة حدوثها.' },
      new_enquiry: { title: 'استفسار جديد من الموقع', hint: 'عندما يملأ أحدهم نموذج التواصل.' },
      staff_equipment_overdue: { title: 'معدات لم تُعَد', hint: 'ملخص صباحي، عند تأخر معدات.' },
      event_invited: { title: 'دعوة إلى موعد', hint: 'عندما يضيفك المكتب إلى موعد.' },
      leave_decided: { title: 'الرد على طلب إجازة أو إذن', hint: 'عندما يُوافَق على طلبك أو يُرفض.' },
      leave_requested: { title: 'طلب إجازة أو إذن جديد', hint: 'عندما يطلب موظف إجازة أو إذنًا.' },
    },
    timing: {
      job_start: 'قبل كم من الوقت',
      clock_out: 'بعد كم من الوقت من الانتهاء المخطط',
      document_expiring: 'قبل كم من الوقت من انتهاء الصلاحية',
      equipment_due: 'قبل كم من الوقت من موعد الإرجاع',
      staff_document_expiring: 'قبل كم من الوقت من انتهاء الصلاحية',
      missed_clock_in: 'بعد كم من الوقت من البدء',
    },
    device: {
      title: 'الإشعارات الفورية على هذا الجهاز',
      on: 'مفعّلة: تصلك التذكيرات هنا حتى والبوابة مغلقة.',
      off: 'احصل على التذكيرات على هذا الجهاز حتى والبوابة مغلقة.',
      denied: 'الإشعارات لهذا الموقع محظورة في إعدادات المتصفح. ارفع الحظر من هناك ثم عد.',
      installFirst: 'على iPhone وiPad: اضغط مشاركة ← إضافة إلى الشاشة الرئيسية، وافتح البوابة من هناك، ثم فعّلها.',
      unsupported: 'لا يستطيع هذا المتصفح تلقي الإشعارات الفورية.',
      turnOn: 'تفعيل',
      turnOff: 'إيقاف',
      failed: 'تعذّر التفعيل. حاول مرة أخرى.',
    },
    multiple: 'يمكنك اختيار أكثر من خيار.',
    how: 'أرسل أيضًا عبر',
    alsoInBell: 'ودائمًا تحت الجرس.',
    onlyInBell: 'تحت الجرس فقط: بلا بريد إلكتروني وبلا إشعار فوري.',
    channels: { in_app: 'البوابة والتطبيق', email: 'البريد الإلكتروني', push: 'إشعار فوري' },
    always: 'مفعّل دائمًا',
    emailPending: 'إرسال البريد الإلكتروني قيد الإعداد — وإلى ذلك الحين ستجدها هنا.',
    pushMissing: 'للإشعارات الفورية، فعّلها لهذا الجهاز أعلاه، أو ثبّت تطبيق HH Electrical Systems على هاتفك.',
    minutes: '{count} دقيقة',
    hourOne: 'ساعة واحدة',
    hours: '{count} ساعات',
    dayOne: 'يوم واحد',
    days: '{count} أيام',
    sameDay: 'في اليوم نفسه',
    atEnd: 'عند الانتهاء',
    quiet: 'عدم الإزعاج',
    quietHint: 'خلال هذه الساعات ينتظر البريد الإلكتروني والإشعار الفوري؛ وتصل الإشعارات هنا مع ذلك. بتوقيت إيطاليا.',
    quietOn: 'مفعّل',
    from: 'من',
    to: 'إلى',
    emailLanguage: 'لغة البريد الإلكتروني',
    emailLanguageHint: 'هنا تتبع الإشعارات اللغة التي اخترتها؛ وتستخدم رسائل البريد الإلكتروني هذه اللغة.',
    save: 'حفظ التفضيلات',
    saved: 'تم حفظ التفضيلات.',
    discard: 'تجاهل التغييرات',
    on: 'مفعّل',
    off: 'متوقف',
  },
  account: {
    passkeyBlockedByApp: 'مع تفعيل تطبيق المصادقة لا يمكنك تسجيل الدخول بمفتاح المرور: خدمة تسجيل الدخول من Amazon لا تسمح بالاثنين معًا. ومفتاح المرور (البصمة أو الوجه أو رقم PIN) يُعدّ بحد ذاته تحققًا بخطوتين، فيمكنك إيقاف التطبيق دون خسارة في الأمان.',
    turnOffAppForPasskey: 'أوقف التطبيق واستخدم مفتاح المرور',
    passkeyReady: 'تم: يمكنك الآن تسجيل الدخول بمفتاح المرور.',
    appDisablesPasskeys: 'مع تفعيل تطبيق المصادقة لن تستطيع تسجيل الدخول بمفاتيح المرور (بل بكلمة المرور والرمز فقط). هل تتابع؟',
    tab: 'الحساب',
    tabRules: 'قواعد تسجيل الدخول للعمل',
    security: 'تسجيل الدخول والأمان',
    securityHint: 'كيف تدخل إلى البوابة. ليس أي منها إلزاميًا، لكن مفتاح المرور هو الأسهل والأكثر أمانًا.',
    passkeys: 'مفاتيح المرور',
    passkeysHint: 'سجّل الدخول ببصمتك أو وجهك أو رقم PIN للجهاز — بلا كلمة مرور ولا رموز. مفتاح لكل جهاز تستخدمه.',
    addPasskey: 'إضافة مفتاح مرور',
    noPasskeys: 'لا توجد مفاتيح مرور بعد.',
    unnamed: 'مفتاح مرور',
    addedOn: 'أُضيف في {date}',
    remove: 'إزالة',
    removeConfirm: 'إزالة مفتاح المرور هذا؟ سيحتاج ذلك الجهاز إلى كلمة مرورك من جديد.',
    passkeyAdded: 'تمت إضافة مفتاح المرور.',
    unsupported: 'هذا المتصفح لا يدعم مفاتيح المرور.',
    app: 'تطبيق المصادقة',
    appHint: 'بعد كلمة المرور سيُطلب منك رمز من 6 أرقام. لا يُطلب عند تسجيل الدخول بمفتاح المرور.',
    on: 'مفعّل',
    off: 'متوقف',
    setUp: 'إعداد',
    turnOff: 'إيقاف',
    turnOffConfirm: 'إيقاف تطبيق المصادقة؟ كلمة المرور وحدها تحمي الحساب بدرجة أقل.',
    enabled: 'تم تفعيل تطبيق المصادقة.',
    disabled: 'تم إيقاف تطبيق المصادقة.',
    cancel: 'إلغاء',
    password: 'كلمة المرور',
    passwordHint: '12 حرفًا على الأقل، مع حرف كبير وحرف صغير ورقم.',
    currentPassword: 'كلمة المرور الحالية',
    newPassword: 'كلمة المرور الجديدة',
    confirmPassword: 'تأكيد كلمة المرور',
    changePassword: 'تغيير كلمة المرور',
    passwordChanged: 'تم تحديث كلمة المرور.',
    mismatch: 'كلمتا المرور غير متطابقتين.',
  },
};
